import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import styled, { keyframes } from "styled-components";
//import QRCode from "react-qr-code";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { Helmet } from "react-helmet";

const DEFAULT_AVATAR = "/images/default-avatar.png";
const WINNER_CONFIG = {
  weekly: {
    count: 3,
    intervalMinutes: 0.25, // 2,
  },
  allTime: {
    count: 3,
    intervalMinutes: 0.5, // 5,
  },
  durationSeconds: 12,
};

function getLastCompletedWeek() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const getPart = (type) =>
    Number(parts.find((part) => part.type === type)?.value);

  // UTC is used here for calendar arithmetic, avoiding DST changes.
  const currentSunday = new Date(
    Date.UTC(getPart("year"), getPart("month") - 1, getPart("day")),
  );

  currentSunday.setUTCDate(
    currentSunday.getUTCDate() - currentSunday.getUTCDay(),
  );

  const from = new Date(currentSunday);
  from.setUTCDate(from.getUTCDate() - 7);

  const to = new Date(currentSunday);
  to.setUTCDate(to.getUTCDate() - 1);

  return {
    fromDate: from.toISOString().slice(0, 10),
    toDate: to.toISOString().slice(0, 10),
  };
}

const Page = styled.div`
  min-height: 100vh;
  padding: 22px 18px 34px;
  background: linear-gradient(135deg, #232d3d 0%, #2c374a 50%, #1b2230 100%);
  color: #e5e7eb;
  font-family: Arial, sans-serif;
  direction: ${({ rtl }) => (rtl ? "rtl" : "ltr")};
  text-align: ${({ rtl }) => (rtl ? "right" : "left")};
  box-sizing: border-box;
`;

const HeroQR = styled.div`
  width: 196px;
  box-sizing: border-box;

  background: #ffffff;
  padding: 8px;
  border-radius: 12px;

  display: flex;
  flex-direction: column;
  align-items: center;

  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);

  flex-shrink: 0;

  svg {
    display: block;
    flex-shrink: 0;
    width: 180px !important;
    height: 180px !important;
    max-width: none;
    max-height: none;
  }
`;

const HeroQRBlock = styled.div`
  position: absolute;
  right: 24px;
  top: 76px;
  bottom: auto;

  width: 196px;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const QRText = styled.div`
  margin-top: 6px;
  font-size: 10px;
  font-weight: 700;
  color: #111827;
  text-align: center;
`;

const PoweredBy = styled.div`
  margin-top: 7px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;

  direction: ltr;
  white-space: nowrap;

  /*
   * No background-the hero image remains visible.
   */
  background: transparent;

  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.9));
`;

const PoweredByText = styled.span`
  color: rgba(255, 255, 255, 0.92);
  font-size: 9px;
  font-weight: 700;
`;

const PoweredByLogo = styled.img`
  width: 52px;
  height: auto;
  object-fit: contain;
`;

const Card = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 22px;
  border-radius: 28px;
  background: rgba(0, 0, 0, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-sizing: border-box;
`;

const TopRightButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
`;

const ChallengeAdLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  width: fit-content;
  padding: 7px 12px;
  border: 1px solid rgba(101, 189, 255, 0.5);
  border-radius: 8px;
  background: #007df4;
  color: white;
  font-size: 13px;
  line-height: 1.2;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: #0068cc;
    color: white;
    text-decoration: none;
  }
`;

const Logo = styled.img`
  width: 95px;
  height: auto;
  margin-left: 0;
  opacity: 0.9;
`;

const CircleLogoContainer = styled.div`
  width: 190px;
  height: 90px;
  box-sizing: border-box;
  padding: 6px 8px;

  display: flex;
  align-items: center;
  justify-content: center;

  //background: #ffffff;
  background: black;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 10px;
  overflow: hidden;

  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
`;

const CircleLogo = styled.img`
  width: 100%;
  height: 100%;
  display: block;

  object-fit: contain;
  object-position: center;

  //background: #ffffff;
  background: black;
`;

const Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin: 14px 0 8px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(56, 189, 248, 0.55)" : "rgba(255, 255, 255, 0.08)"};

  border-radius: 999px;
  padding: 7px 13px;

  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  font-weight: 650;

  color: ${({ active }) => (active ? "#ffffff" : "#b9c1cc")};

  background: ${({ active }) =>
    active ? "rgba(71, 85, 105, 0.72)" : "rgba(255, 255, 255, 0.055)"};

  box-shadow: ${({ active }) =>
    active ? "inset 0 0 0 1px rgba(56, 189, 248, 0.08)" : "none"};

  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: ${({ active }) =>
      active ? "rgba(71, 85, 105, 0.9)" : "rgba(255, 255, 255, 0.09)"};
  }

  @media (max-width: 560px) {
    padding: 7px 11px;
    font-size: 14px;
  }
`;

const JoinToast = styled.div`
  position: fixed;
  left: 50%;
  bottom: 26px;
  transform: translateX(-50%);
  z-index: 99999;

  display: flex;
  align-items: center;
  gap: 12px;

  min-width: 280px;
  max-width: calc(100% - 32px);
  padding: 14px 18px;
  border-radius: 999px;

  background: rgba(15, 23, 42, 0.96);
  border: 1px solid rgba(56, 189, 248, 0.45);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);

  color: white;
  animation: joinToastIn 0.35s ease-out;

  @keyframes joinToastIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(30px) scale(0.96);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0) scale(1);
    }
  }
`;

const JoinToastAvatar = styled.img`
  width: 42px;
  height: 42px;
  border-radius: 999px;
  object-fit: cover;
  background: #111827;
`;

const JoinToastFallback = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 999px;
  background: #38bdf8;
  color: #020617;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
`;

const JoinToastText = styled.div`
  display: flex;
  flex-direction: column;
`;

const JoinToastTitle = styled.div`
  font-weight: 900;
  font-size: 15px;
`;

const JoinToastSub = styled.div`
  color: #94a3b8;
  font-size: 13px;
  margin-top: 2px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 76px 110px minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;

  box-sizing: border-box;
  height: 112px;
  min-height: 112px;

  margin-bottom: 9px;
  padding: 0 18px 0 0;

  /* Keep the outer padding on the score side in either language. */
  padding-inline-start: 0;
  padding-inline-end: 18px;

  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 17px;

  direction: ${({ $rtl }) => ($rtl ? "rtl" : "ltr")};

  opacity: 0;
  animation: rowEnter 0.5s ease-out forwards;
  animation-delay: ${({ index = 0 }) => `${Math.min(index, 12) * 70}ms`};

  transition:
    transform 0.35s ease,
    background 0.35s ease,
    box-shadow 0.35s ease;

  ${({ podiumRank, isLastFixedLeader }) =>
    podiumRank === 1 &&
    `
      background: linear-gradient(
        90deg,
        rgba(250, 204, 21, 0.16),
        rgba(250, 204, 21, 0.035)
      );

      box-shadow: inset 3px 0 0 rgba(250, 204, 21, 0.85);

      margin-bottom: ${isLastFixedLeader ? "14px" : "9px"};
    `}

  ${({ podiumRank, isLastFixedLeader }) =>
    podiumRank === 2 &&
    `
      background: linear-gradient(
        90deg,
        rgba(203, 213, 225, 0.12),
        rgba(203, 213, 225, 0.025)
      );

      box-shadow: inset 3px 0 0 rgba(203, 213, 225, 0.7);

      margin-bottom: ${isLastFixedLeader ? "14px" : "9px"};
    `}

  ${({ isFixedLeader, podiumRank, isLastFixedLeader }) =>
    isFixedLeader &&
    podiumRank >= 3 &&
    `
      background: linear-gradient(
        90deg,
        rgba(217, 119, 6, 0.13),
        rgba(217, 119, 6, 0.025)
      );

      box-shadow: inset 3px 0 0 rgba(217, 119, 6, 0.72);

      margin-bottom: ${isLastFixedLeader ? "14px" : "9px"};
    `}

  ${({ updated }) =>
    updated &&
    `
      animation:
        rowEnter 0.5s ease-out forwards,
        activityPulse 4s ease-out;

      background: rgba(34, 197, 94, 0.12);
      box-shadow:
        inset 4px 0 0 #22c55e,
        0 0 24px rgba(34, 197, 94, 0.18);
    `}

  @keyframes activityPulse {
    0% {
      background: rgba(34, 197, 94, 0.04);
    }

    20% {
      background: rgba(34, 197, 94, 0.22);
    }

    100% {
      background: rgba(34, 197, 94, 0);
    }
  }

  @keyframes rowEnter {
    from {
      opacity: 0;
      transform: translateY(14px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 560px) {
    grid-template-columns: 48px 78px minmax(0, 1fr) auto;
    gap: 8px;

    height: 112px;
    min-height: 112px;

    padding: 0;
    padding-inline-end: 10px;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
    transition: none;
  }
`;

const Rank = styled.div`
  width: 60px;
  height: 60px;
  justify-self: center;

  display: grid;
  place-items: center;

  background: rgba(148, 163, 184, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 8px;

  color: ${({ $rank }) =>
    $rank === 1
      ? "#facc15"
      : $rank === 2
        ? "#e2e8f0"
        : $rank === 3
          ? "#f0ad70"
          : "#ffffff"};

  direction: ltr;
  font-size: 34px;
  font-weight: 900;
  line-height: 1;
  font-variant-numeric: tabular-nums;

  @media (max-width: 560px) {
    width: 40px;
    height: 48px;
    font-size: 26px;
    border-radius: 6px;
  }
`;

const Avatar = styled.img`
  display: block;
  box-sizing: border-box;

  width: 110px;
  height: 110px;

  object-fit: cover;
  object-position: center;
  background: #111827;

  border: none;
  border-radius: 0;

  @media (max-width: 560px) {
    width: 78px;
    height: 110px;
  }
`;

const MemberInfo = styled.div`
  min-width: 0;
  text-align: start;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  /* Let long club names truncate within this column. */
  > div {
    max-width: 100%;
    min-width: 0;
  }
`;

const Name = styled.div`
  max-width: 100%;
  color: #ffffff;

  font-size: 26px;
  font-weight: 800;
  line-height: 1.2;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &&,
  && bdi {
    font-family: Arial, sans-serif !important;
    font-weight: 700;
  }

  @media (max-width: 560px) {
    font-size: 18px;
  }
`;

const MusicStrip = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin: 18px 0;
  padding: 24px;
  border: 1px solid rgba(167, 139, 250, 0.4);
  border-radius: 20px;
  background: linear-gradient(120deg, #30204b, #152238);
  color: #fff;

  @media (max-width: 480px) {
    gap: 14px;
    padding: 16px;
  }
`;

const MusicDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const MusicEyebrow = styled.div`
  color: #d8c3ff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  margin-bottom: 14px;
`;

const MusicLabel = styled.div`
  color: #c4b5fd;
  font-size: 12px;
  margin-bottom: 4px;
`;

const MusicTitle = styled.div`
  font-size: clamp(18px, 2.2vw, 30px);
  font-weight: 800;
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

const MusicArtist = styled.div`
  margin-top: 6px;
  color: #e2d9f3;
  font-size: 15px;
  overflow-wrap: anywhere;
`;

const MusicUpcoming = styled.div`
  margin-top: 12px;
  color: #bcc8dc;
  font-size: 12px;
  overflow-wrap: anywhere;
`;

const MusicQRBlock = styled.div`
  flex-shrink: 0;
  width: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;

  > div {
    padding: 6px;
    background: #fff;
    border-radius: 12px;
  }

  svg,
  canvas {
    display: block;
  }

  strong {
    font-size: 12px;
    color: #fff;
  }

  small {
    font-size: 10px;
    color: #c4cfe1;
  }

  @media (max-width: 480px) {
    width: 120px;
  }
`;

const AvatarFallback = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 22px;
`;

const CLUB_COLORS = [
  "#2563eb", // Blue
  "#dc2626", // Red
  "#15803d", // Green
  "#9333ea", // Purple
  "#c2410c", // Orange
  "#be185d", // Pink
  "#0f766e", // Teal
];

const CircleTag = styled.div`
  display: inline-block;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: 5px;
  padding: 4px 9px;

  border-radius: 4px;
  border: none;
  background: ${({ $color }) => $color || "#0369a1"};
  color: #ffffff;

  font-size: 12px;
  font-weight: 700;
  line-height: 1.3;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.div`
  color: #94a3b8;
  font-size: 13px;
  margin-top: 3px;
`;

const Score = styled.div`
  color: #ffffff;
  background: transparent;
  border: none;
  padding: 0;
  box-shadow: none;

  font-size: ${({ $topThree }) => ($topThree ? "36px" : "32px")};
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.5px;
  font-variant-numeric: tabular-nums;

  text-align: center;
  white-space: nowrap;

  @media (max-width: 560px) {
    font-size: ${({ $topThree }) => ($topThree ? "28px" : "24px")};
  }
`;

const ScoreLabel = styled.div`
  margin-top: 4px;
  color: #a8b4c5;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
`;

const LiveIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const LiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 rgba(34, 197, 94, 0.5);
  animation: livePulse 1.8s infinite;

  @keyframes livePulse {
    0% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55);
    }

    70% {
      box-shadow: 0 0 0 8px rgba(34, 197, 94, 0);
    }

    100% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Empty = styled.div`
  padding: 30px 0;
  text-align: center;
  color: #94a3b8;
`;

const Copyright = styled.div`
  text-align: center;

  color: rgba(229, 231, 235, 0.5);

  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.2px;

  white-space: nowrap;
`;

const TabsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -2px 0 6px;
  flex-wrap: wrap;
`;

const Switch = styled.input`
  width: 15px;
  height: 15px;
  cursor: pointer;
`;

const SettingsButton = styled.button`
  border: none;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(255, 255, 255, 0.1);
  color: #e5e7eb;

  font-size: 18px;
  font-weight: 900;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalCard = styled.div`
  position: relative;
  box-sizing: border-box;
  width: calc(100% - 32px);
  max-width: ${({ $wide }) => ($wide ? "740px" : "380px")};
  max-height: 88vh;
  overflow-y: auto;
  overflow-x: hidden;

  padding: 22px 18px 18px;
  border-radius: 20px;
  background: #111827;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: #e5e7eb;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);

  scrollbar-width: thin;
  scrollbar-color: #475569 transparent;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: #475569;
    border-radius: 999px;
  }

  @media (max-width: 600px) {
    padding: 24px 16px 16px;
    max-height: 90vh;
  }
`;

const SettingsLayout = styled.div`
  column-count: 2;
  column-gap: 10px;
  margin-top: 12px;

  > * {
    display: inline-block;
    vertical-align: top;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    break-inside: avoid;
    margin: 0 0 8px;
    padding: 10px 12px;
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 9px;
    background: #182233;
  }

  > label {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    min-height: 42px;
    gap: 10px;
    font-size: 12px;
    line-height: 1.4;
  }

  select,
  input[type="date"],
  input[type="time"] {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    min-height: 32px;
    padding: 5px 9px;
    border-radius: 7px;
    font-size: 12px;
    color-scheme: dark;
  }

  input:focus-visible,
  select:focus-visible {
    outline: 2px solid #60a5fa;
    outline-offset: 2px;
  }

  @media (max-width: 600px) {
    column-count: 1;
  }
`;

const SettingsFooter = styled.div`
  position: sticky;
  bottom: -18px;
  z-index: 20;
  margin-top: 8px;
  padding: 10px 0 4px;
  background: #111827;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  button {
    min-height: 36px;
    border-radius: 8px;
    background: #c8102e;
    color: #fff;
    font-size: 13px;
  }

  @media (max-width: 600px) {
    bottom: -16px;
  }
`;

const ModalTitle = styled.h3`
  margin: 0;
  padding-inline-end: 55px;
  color: #fff;
  font-size: 20px;
  line-height: 1.4;
  font-weight: 700;
`;

const MetricItem = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  font-size: 12px;
  line-height: 1.25;
  font-weight: 600;

  color: #e5e7eb;
  cursor: pointer;

  input {
    margin: 0;
  }
`;

const SettingRow = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;

  min-height: 28px;
  padding: 1px 0;
  gap: 8px;

  font-size: 12px;
  line-height: 1.25;
  font-weight: 500;
`;

const CloseModalButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 999px;

  padding: 7px 12px;
  margin-top: 8px;

  font-size: 13px;
  font-weight: 800;
  cursor: pointer;

  position: sticky;
  bottom: 0;
  z-index: 10;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 14px;
  margin-top: 4px;
`;

const Hero = styled.div`
  position: relative;
  height: 320px;

  margin-top: -22px;
  margin-left: -22px;
  margin-right: -22px;

  border-radius: 28px 28px 0 0;
  overflow: hidden;

  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.25),
    rgba(0, 0, 0, 0.65)
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  padding: 20px 24px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const HeroTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  /*
   * Keep fixed controls in the same positions:
   * Circle logo left, settings right.
   */
  direction: ltr;
  position: relative;
  z-index: 5;
`;

const HeroBottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ rtl }) => (rtl ? "flex-end" : "flex-start")};
  text-align: ${({ rtl }) => (rtl ? "right" : "left")};
  margin-bottom: 40px;
`;

const HeroTitle = styled.h1`
  margin: 0;
  color: white;
  font-size: 42px;
  font-weight: 800;
`;

const ScoreArea = styled.div`
  direction: ltr;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-width: 140px;

  @media (max-width: 560px) {
    min-width: 82px;
  }
`;

const ScoreChange = styled.div`
  padding: 4px 9px;
  border-radius: 999px;

  color: #86efac;
  background: rgba(34, 197, 94, 0.15);
  border: 1px solid rgba(34, 197, 94, 0.35);

  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;

  animation: scoreChangePulse 4s ease-out forwards;

  @keyframes scoreChangePulse {
    0% {
      opacity: 0;
      transform: translateY(7px) scale(0.85);
    }

    12% {
      opacity: 1;
      transform: translateY(0) scale(1.08);
    }

    25% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.55);
    }

    55% {
      box-shadow: 0 0 0 12px rgba(34, 197, 94, 0);
    }

    80% {
      opacity: 1;
    }

    100% {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const HeroSubtitle = styled.div`
  color: rgba(255, 255, 255, 0.85);
  font-size: 16px;
  margin-top: 8px;
`;

const HeroStats = styled.div`
  color: white;
  margin-top: 10px;
  font-size: 14px;
`;

const SettingColumn = styled.div`
  padding: 4px 0;
`;

const SettingLabel = styled.div`
  color: #f8fafc;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 700;
  margin-bottom: 3px;
`;

const ActivitySelect = styled.select`
  width: 100%;
  height: 34px;

  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;

  padding: 0 10px;

  font-size: 12px;
  line-height: 34px;

  background: #0f172a;
  color: #e5e7eb;

  font-weight: 700;
  outline: none;
`;

const DateRangeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 10px;
`;

const DateField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 700;
`;

const DateInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  padding: 8px 10px;
  background: #0f172a;
  color: #e5e7eb;
  font-size: 13px;
  color-scheme: dark;
  outline: none;
`;

const DateError = styled.div`
  margin-top: 7px;
  color: #fca5a5;
  font-size: 12px;
`;

const AnimatedNumber = ({ value, formatValue }) => {
  const numericValue = Number(value) || 0;

  const previousValueRef = useRef(null);
  const [displayValue, setDisplayValue] = useState(numericValue);

  useEffect(() => {
    const previousValue = previousValueRef.current;

    // First load: show the current value immediately
    if (previousValue === null) {
      previousValueRef.current = numericValue;
      setDisplayValue(numericValue);
      return;
    }

    // No change: keep the current value
    if (previousValue === numericValue) {
      setDisplayValue(numericValue);
      return;
    }

    // Refresh with changed data: start 50 below the new value
    const startValue = Math.max(0, numericValue - 50);
    const duration = 1800;
    const startTime = performance.now();

    let animationFrame;

    setDisplayValue(startValue);

    const animate = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      const animatedValue =
        startValue + (numericValue - startValue) * easedProgress;

      setDisplayValue(animatedValue);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    previousValueRef.current = numericValue;
    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [numericValue]);

  return formatValue(displayValue);
};

const LaunchGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const LaunchScreen = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
  padding: 32px 16px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  gap: 28px;
  text-align: center;
`;

const LaunchName = styled.h1`
  margin: 0;
  color: #fff;
  font-size: clamp(28px, 5vw, 58px);
  font-weight: 900;
`;

const LaunchLabel = styled.div`
  color: #cbd5e1;
  font-family: Arial, sans-serif;
  font-size: clamp(16px, 2vw, 24px);
  font-weight: 400;
`;

const LaunchCountdown = styled.div`
  direction: ltr;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(4px, 0.8vw, 12px);
`;

const CountdownUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const CountdownNumber = styled.div`
  min-width: 1.8em;
  padding: 10px 8px;
  border-radius: 14px;
  font-size: clamp(24px, 4.5vw, 64px);

  background: linear-gradient(160deg, #183653, #0b1728);
  border: 1px solid rgba(56, 189, 248, 0.4);

  color: #7dd3fc;
  font-family: "Courier New", monospace;

  font-weight: 900;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  text-align: center;

  text-shadow: 0 0 20px rgba(56, 189, 248, 0.65);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    0 8px 25px rgba(0, 0, 0, 0.3),
    0 0 28px rgba(0, 125, 244, 0.12);
`;

const CountdownUnitLabel = styled.div`
  color: #94a3b8;
  font-size: clamp(10px, 1.2vw, 14px);
  font-weight: 700;
`;

const CountdownSeparator = styled.span`
  color: #38bdf8;
  font-weight: 900;
  font-size: clamp(20px, 3vw, 42px);
  margin-bottom: 20px;
`;

const LaunchQR = styled.div`
  margin-top: 0;
  padding: 12px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.3);

  canvas {
    width: clamp(130px, 23vh, 200px);
    height: auto;
  }
`;

const LaunchInvite = styled.div`
  color: #fff;
  font-family: Arial, sans-serif;
  font-size: clamp(18px, 2.5vw, 28px);
  font-weight: 400;
`;

const MusicRibbon = styled.section`
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 20px;

  & * {
    font-family: inherit;
  }

  margin: 24px 0 8px;
  padding: 18px 22px;
  min-width: 0;

  color: #fff;
  background: #c8102e;
  border: none;
  border-radius: 14px;
  box-shadow: none;

  @media (max-width: 600px) {
    padding: 14px;
    gap: 12px;
  }
`;

const MusicRibbonIcon = styled.div`
  width: 54px;
  height: 54px;
  flex-shrink: 0;
  display: grid;
  place-items: center;

  color: #fff;
  border-radius: 17px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.24),
    rgba(255, 255, 255, 0.08)
  );
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 8px 20px rgba(45, 0, 5, 0.25);

  transform: rotate(-7deg);

  svg {
    width: 29px;
    height: 29px;
    transform: rotate(7deg);
  }

  @media (max-width: 600px) {
    width: 40px;
    height: 40px;
    border-radius: 12px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const MusicRibbonInfo = styled.div`
  flex: 1;
  min-width: 0;

  small {
    display: inline-block;
    margin-bottom: 8px;
    padding: 4px 9px;
    border-radius: 999px;
    background: rgba(45, 0, 5, 0.28);
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: #ede9fe;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.06em;
  }

  > strong {
    display: block;
    width: 100%;
    margin-bottom: 12px;

    font-family: Arial, sans-serif;
    font-size: clamp(22px, 2.5vw, 30px);
    font-weight: 700;
    line-height: 1.3;

    color: #fff;
    text-align: start;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  p {
    margin: 7px 0 0;
    color: #ffe4e6;
    font-size: 13px;
    line-height: 1.5;
  }
`;

const MusicRibbonQR = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-shrink: 0;

  span {
    max-width: none;
    font-family: Arial, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.3;
    text-align: center;
  }

  a {
    display: block;
    flex-shrink: 0;
    padding: 8px;
    background: #ffffff;
    border-radius: 8px;
    line-height: 0;
    box-shadow: none;
  }

  svg {
    display: block;
    flex-shrink: 0;
    width: 220px !important;
    height: 220px !important;
    max-width: none;
    max-height: none;
  }

  @media (max-width: 520px) {
    span {
      display: none;
    }
  }
`;

const MusicRequester = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 12px;
  min-width: 0;
  color: #fff;

  .requester-avatar {
    width: 110px;
    height: 110px;
    box-sizing: border-box;
    flex-shrink: 0;
    display: block;
    border-radius: 8px;
    object-fit: cover;
    object-position: center;
    border: 2px solid rgba(255, 255, 255, 0.65);
  }

  .requester-fallback {
    display: grid;
    place-items: center;
    background: rgba(255, 255, 255, 0.16);
    font-size: 30px;
    font-weight: 700;
  }

  .requester-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    font-family: Arial, sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.3;
  }

  @media (max-width: 600px) {
    gap: 10px;

    .requester-avatar {
      width: 60px;
      height: 60px;
    }

    .requester-name {
      font-size: 16px;
    }
  }

  .requester-details {
    flex: 1;
    min-width: 0;
    text-align: start;
  }

  .requester-song {
    margin-bottom: 8px;
    color: #fff;
    font-family: Arial, sans-serif;
    font-size: 28px;
    font-weight: 700;
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .requester-name {
    color: rgba(255, 255, 255, 0.85);
    font-family: Arial, sans-serif;
    font-size: 20px;
    font-weight: 600;
  }

  @media (max-width: 600px) {
    .requester-song {
      font-size: 20px;
    }

    .requester-name {
      font-size: 15px;
    }
  }
`;

function MusicSongRequester({ user, rtlMode, songTitle }) {
  const [imageFailed, setImageFailed] = useState(false);

  const requester = user && typeof user === "object" ? user : null;
  const name = requester?.nickname || requester?.name || "";
  const rawImage = String(requester?.img_url || "").trim();

  const imageUrl = rawImage
    ? /^https?:\/\//i.test(rawImage)
      ? rawImage
      : `https://bealyo-upload-files.s3.us-east-2.amazonaws.com/${rawImage.replace(/^\/+/, "")}`
    : "";

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  if (!requester || !name) return null;

  return (
    <MusicRequester dir={rtlMode ? "rtl" : "ltr"}>
      {imageUrl && !imageFailed ? (
        <img
          className="requester-avatar"
          src={imageUrl}
          alt=""
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span
          className="requester-avatar requester-fallback"
          aria-hidden="true"
        >
          {Array.from(name.trim())[0] || "♫"}
        </span>
      )}

      <div className="requester-details">
        <div className="requester-song" dir="auto">
          {songTitle}
        </div>

        <div className="requester-name">
          {rtlMode ? "לבקשת " : "Requested by "}
          <bdi dir="auto">{name}</bdi>
        </div>
      </div>
    </MusicRequester>
  );
}

function getIsraelLaunchTimestamp(date, time) {
  if (!date || !/^\d{2}:\d{2}$/.test(time || "")) return NaN;

  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.split(":").map(Number);

  if (hour > 23 || minute > 59) return NaN;

  const target = Date.UTC(year, month - 1, day, hour, minute);

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Jerusalem",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

  const getParts = (timestamp) =>
    Object.fromEntries(
      formatter
        .formatToParts(new Date(timestamp))
        .filter((part) => part.type !== "literal")
        .map((part) => [part.type, Number(part.value)]),
    );

  let timestamp = target;

  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = getParts(timestamp);

    const displayed = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    );

    const correction = target - displayed;

    if (correction === 0) break;
    timestamp += correction;
  }

  const result = getParts(timestamp);

  // Reject invalid dates and times skipped by daylight-saving changes.
  if (
    result.year !== year ||
    result.month !== month ||
    result.day !== day ||
    result.hour !== hour ||
    result.minute !== minute
  ) {
    return NaN;
  }

  return timestamp;
}

const winnersFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const winnersFadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const winnersCardIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px) scale(0.94);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const winnersCardOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }
`;

const winnerUserIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const WinnersOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 20px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.94);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);

  animation: ${({ $closing }) => ($closing ? winnersFadeOut : winnersFadeIn)}
    400ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const WinnersCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 680px;
  max-height: none;
  overflow: visible;
  box-sizing: border-box;

  padding: 24px 18px;
  border-radius: 22px;
  border: 1px solid rgba(250, 204, 21, 0.4);
  background: #151d29;

  color: white;
  text-align: center;
  font-family: Arial, sans-serif;
  direction: ${({ $rtl }) => ($rtl ? "rtl" : "ltr")};

  box-shadow: 0 24px 90px rgba(0, 0, 0, 0.6);

  animation: ${({ $closing }) => ($closing ? winnersCardOut : winnersCardIn)}
    400ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-height: 700px) {
    max-width: 600px;
    padding: 14px 16px;
    border-radius: 16px;

    h2 {
      margin: 8px 24px !important;
      font-size: 20px !important;
    }
  }
`;

const WinnerUser = styled.div`
  min-width: 0;
  padding: 14px 10px;
  box-sizing: border-box;

  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;

  animation: ${winnerUserIn} 500ms ease both;
  animation-delay: ${({ $index }) => `${200 + $index * 160}ms`};

  @media (max-width: 480px) {
    padding: 12px 6px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-height: 700px) {
    padding: 10px 6px;
  }
`;

const WinnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $count }) => $count}, minmax(0, 1fr));
  gap: 12px;
  margin-top: 20px;

  @media (max-width: 480px) {
    gap: 8px;
  }

  @media (max-height: 700px) {
    margin-top: 10px;
    gap: 10px;
  }
`;

const WinnerAvatar = styled.img`
  display: block;
  width: 120px;
  height: 120px;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;

  border-radius: 8px;
  object-fit: cover;
  background: #1e293b;

  border: 2px solid
    ${({ $rank }) =>
      $rank === 1 ? "#facc15" : $rank === 2 ? "#cbd5e1" : "#d97706"};

  @media (max-height: 700px) {
    width: 18vh;
    height: 18vh;
    max-width: 100%;
  }
`;

const WinnerName = styled.div`
  margin-top: 12px;
  font-size: clamp(12px, 1.8vw, 18px);
  font-weight: 800;
  line-height: 1.25;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &&,
  && bdi {
    font-family: Arial, sans-serif !important;
    font-weight: 700;
  }
  @media (max-height: 700px) {
    margin-top: 8px;
    font-size: 16px;
  }
`;

const WinnerSteps = styled.div`
  margin-top: 16px;
  color: #ffffff;
  font-size: clamp(22px, 3vw, 34px);
  font-weight: 900;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  direction: ltr;

  small {
    display: block;
    margin-top: 5px;
    color: #a8b4c5;
    font-family: Arial, sans-serif;
    font-size: 12px;
    font-weight: 400;
  }

  @media (max-height: 700px) {
    margin-top: 8px;
    font-size: 26px;

    small {
      margin-top: 3px;
      font-size: 11px;
    }
  }
`;

const WinnerRank = styled.div`
  display: grid;
  place-items: center;

  width: 48px;
  height: 48px;
  margin: 0 auto 14px;
  border-radius: 8px;

  background: rgba(148, 163, 184, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.09);

  color: ${({ $rank }) =>
    $rank === 1 ? "#facc15" : $rank === 2 ? "#e2e8f0" : "#f0ad70"};

  font-size: 30px;
  font-weight: 900;
  line-height: 1;

  @media (max-height: 700px) {
    width: 34px;
    height: 34px;
    margin-bottom: 8px;
    font-size: 24px;
  }
`;

const WinnersPeriod = styled.div`
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
  margin: 16px auto 24px;
  padding: 12px 24px;

  background: #303a49;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;

  color: #ffffff;
  font-family: Arial, sans-serif;
  text-align: center;

  small {
    display: block;
    margin-bottom: 6px;
    color: #cbd5e1;
    font-size: 13px;
  }

  strong {
    display: block;
    font-size: clamp(18px, 3vw, 26px);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }

  @media (max-height: 700px) {
    margin: 10px auto 12px;
    padding: 8px 16px;

    small {
      margin-bottom: 3px;
      font-size: 11px;
    }

    strong {
      font-size: 20px;
    }
  }
`;

const WinnersClose = styled.button`
  position: absolute;
  top: 12px;
  right: 16px;

  border: none;
  background: transparent;
  color: #cbd5e1;
  font-size: 28px;
  cursor: pointer;
`;

const LeaderboardHeading = styled.div`
  box-sizing: border-box;
  margin: 16px 0 18px;
  padding: 18px 16px;

  background: #252e3b;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;

  text-align: center;

  @media (max-width: 560px) {
    padding: 14px 10px;
  }
`;

const LeaderboardTitle = styled.h2`
  margin: 0;
  color: #ffffff;
  font-size: 40px;
  font-weight: 700;
  line-height: 1.2;
  font-family: Arial, sans-serif;

  @media (max-width: 560px) {
    font-size: 28px;
  }
`;

const LeaderboardSubtitle = styled.div`
  margin-top: 8px;
  color: #b8c3d2;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
`;

function FitToScreen({
  children,
  automatic = true,
  manualScale = 100,
  maxWidth = 1100,
  availableHeight,
}) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  const [layout, setLayout] = useState({
    scale: 1,
    height: 0,
  });

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;

    if (!container || !content) return;

    let frame;

    const measure = () => {
      const width = content.offsetWidth;
      const height = content.offsetHeight;

      if (!width || !height) return;

      const viewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;

      // Leave room for page padding and copyright.
      const heightLimit = availableHeight || Math.max(1, viewportHeight - 90);

      const scale = automatic
        ? Math.min(container.clientWidth / width, heightLimit / height, 1)
        : manualScale / 100;

      const scaledHeight = Math.ceil(height * scale);

      setLayout((previous) =>
        Math.abs(previous.scale - scale) < 0.0001 &&
        previous.height === scaledHeight
          ? previous
          : { scale, height: scaledHeight },
      );
    };

    const scheduleMeasure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    measure();

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleMeasure)
        : null;

    observer?.observe(container);
    observer?.observe(content);

    window.addEventListener("resize", scheduleMeasure);
    window.visualViewport?.addEventListener("resize", scheduleMeasure);

    // Recheck when portraits or fonts finish loading.
    content.addEventListener("load", scheduleMeasure, true);

    let cancelled = false;

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!cancelled) scheduleMeasure();
      });
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      observer?.disconnect();

      window.removeEventListener("resize", scheduleMeasure);
      window.visualViewport?.removeEventListener("resize", scheduleMeasure);
      content.removeEventListener("load", scheduleMeasure, true);
    };
  }, [automatic, manualScale, availableHeight]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: layout.height || undefined,
        position: "relative",
      }}
    >
      <div
        ref={contentRef}
        style={{
          width: "100%",
          maxWidth,
          margin: "0 auto",
          transform: `scale(${layout.scale})`,
          transformOrigin: "top center",
          display: "flow-root",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function CircleLeaderboard(props) {
  //const circleId = props.match?.params?.id;
  const token = props.match?.params?.token;

  const [canManageSettings, setCanManageSettings] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminSigningIn, setAdminSigningIn] = useState(false);
  const [adminLoginError, setAdminLoginError] = useState("");

  const [topLeadersCount, setTopLeadersCount] = useState(3);
  const [rotatingRowsCount, setRotatingRowsCount] = useState(7);
  const [memberRotationSeconds, setMemberRotationSeconds] = useState(20);
  const [winnerModalClosing, setWinnerModalClosing] = useState(false);
  const [competitionCircleIdsText, setCompetitionCircleIdsText] = useState("");

  const [ownedCircles, setOwnedCircles] = useState([]);

  const cardRef = useRef(null);

  const [memberPage, setMemberPage] = useState(0);

  const [circleData, setCircleData] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const checkAdmin = async () => {
      setCanManageSettings(false);
      setCheckingAdmin(true);

      const savedToken = localStorage.getItem("jwtToken");

      if (!savedToken || !circleData?._id) {
        if (!cancelled) setCheckingAdmin(false);
        return;
      }

      try {
        const { data } = await axios.get(
          process.env.REACT_APP_API_URL + "users/admin/circles",
          {
            headers: { Authorization: savedToken },
          },
        );

        const allowed = (data.circles || []).some(
          (circle) => String(circle._id) === String(circleData._id),
        );

        if (!cancelled) {
          setCanManageSettings(allowed);
        }
      } catch {
        if (!cancelled) setCanManageSettings(false);
      } finally {
        if (!cancelled) setCheckingAdmin(false);
      }
    };

    checkAdmin();

    return () => {
      cancelled = true;
    };
  }, [circleData?._id]);

  const clubColors = useMemo(() => {
    const ids = [
      ...new Set(
        [circleData?._id, ...ownedCircles.map((circle) => circle._id)]
          .filter(Boolean)
          .map(String),
      ),
    ].sort();

    return Object.fromEntries(
      ids.map((id, index) => [id, CLUB_COLORS[index % CLUB_COLORS.length]]),
    );
  }, [circleData?._id, ownedCircles]);

  const [loading, setLoading] = useState(true);
  const [leaderboardUnavailable, setLeaderboardUnavailable] = useState(false);
  const [metric, setMetric] = useState("steps");
  const [leaderboard, setLeaderboard] = useState([]);
  const [stepsLeaderboard, setStepsLeaderboard] = useState([]);

  const [winnerData, setWinnerData] = useState({
    weekly: [],
    allTime: [],
    week: null,
  });

  const [activeWinnerModal, setActiveWinnerModal] = useState(null);

  const [circleNotes, setCircleNotes] = useState([]);
  const [autoRotate, setAutoRotate] = useState(true);
  const [refreshIntervalMinutes, setRefreshIntervalMinutes] = useState(2);
  const [showQR, setShowQR] = useState(true);
  const [challengeAdId, setChallengeAdId] = useState("");
  const [excludeAdmin, setExcludeAdmin] = useState(true);
  const [rtlMode, setRtlMode] = useState(false);
  const [displayScale, setDisplayScale] = useState(100);
  const autoFitStorageKey = `bealyo:leaderboard:autoFit:${token}`;

  const readSavedAutoFit = () => {
    try {
      return localStorage.getItem(autoFitStorageKey) !== "false";
    } catch {
      return true;
    }
  };

  const [autoFit, setAutoFit] = useState(readSavedAutoFit);

  useEffect(() => {
    setAutoFit(readSavedAutoFit());
  }, [token]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState("all");
  const [notesTimeFrame, setNotesTimeFrame] = useState("30");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");

  const [publicMusic, setPublicMusic] = useState(null);
  const currentMusic = publicMusic?.nowPlaying || null;
  const displayedMusic = currentMusic || publicMusic?.queue?.[0] || null;

  const [showWeeklyWinners, setShowWeeklyWinners] = useState(true);
  const [weeklyWinnersIntervalMinutes, setWeeklyWinnersIntervalMinutes] =
    useState(2);

  const [showAllTimeWinners, setShowAllTimeWinners] = useState(true);
  const [allTimeWinnersIntervalMinutes, setAllTimeWinnersIntervalMinutes] =
    useState(5);

  const [countdownNow, setCountdownNow] = useState(() => Date.now());

  // Use the saved date, so unsaved modal edits do not change the screen.
  const savedStartDate = circleData?.leaderboardSettings?.startDate || "";

  // Match the steps API's current UTC day boundary.
  const savedStartTime = circleData?.leaderboardSettings?.startTime || "";

  const launchTimestamp = savedStartDate
    ? savedStartTime
      ? getIsraelLaunchTimestamp(savedStartDate, savedStartTime)
      : new Date(`${savedStartDate}T00:00:00.000Z`).getTime()
    : NaN;

  const isComingSoon =
    Number.isFinite(launchTimestamp) && launchTimestamp > countdownNow;

  const remainingSeconds = isComingSoon
    ? Math.ceil((launchTimestamp - countdownNow) / 1000)
    : 0;

  const countdownDays = String(Math.floor(remainingSeconds / 86400)).padStart(
    2,
    "0",
  );

  const countdownHours = String(
    Math.floor((remainingSeconds % 86400) / 3600),
  ).padStart(2, "0");

  const countdownMinutes = String(
    Math.floor((remainingSeconds % 3600) / 60),
  ).padStart(2, "0");

  const countdownSeconds = String(remainingSeconds % 60).padStart(2, "0");

  const countdownText = `${countdownDays}:${countdownHours}:${countdownMinutes}:${countdownSeconds}`;

  useEffect(() => {
    setCountdownNow(Date.now());

    if (!Number.isFinite(launchTimestamp) || launchTimestamp <= Date.now()) {
      return;
    }

    const timer = setInterval(() => {
      const now = Date.now();
      setCountdownNow(now);

      if (now >= launchTimestamp) {
        clearInterval(timer);

        // Refresh scores immediately when the countdown ends.
        loadCircle(true);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [launchTimestamp, token]);

  const previousScoresRef = useRef(new Map());
  const scoreUpdateTimerRef = useRef(null);

  const [scoreUpdates, setScoreUpdates] = useState({});

  const [visibleMetrics, setVisibleMetrics] = useState({
    steps: true,
    distance: true,
    points: true,
    checkins: true,
  });

  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [settingsChanged, setSettingsChanged] = useState(false);
  const settingsModalOpenRef = useRef(false);

  const hasInvalidDateRange =
    notesTimeFrame === "custom" && (!fromDate || !toDate || fromDate > toDate);

  const circleId = circleData?._id;

  const previousMemberIdsRef = useRef(null);
  const joinToastTimerRef = useRef(null);

  const [joinedUserToast, setJoinedUserToast] = useState(null);

  const metricOrder = useMemo(() => {
    const items = [];

    if (circleData?.showSteps && visibleMetrics.steps) {
      items.push("steps");
    }

    if (visibleMetrics.distance) {
      items.push("distance");
    }

    if (visibleMetrics.points) {
      items.push("points");
    }

    if (visibleMetrics.checkins) {
      items.push("checkins");
    }

    return items;
  }, [
    circleData?.showSteps,
    visibleMetrics.steps,
    visibleMetrics.distance,
    visibleMetrics.points,
    visibleMetrics.checkins,
  ]);

  const circleActivities = useMemo(() => {
    const goals = Array.isArray(circleData?.goals) ? circleData.goals : [];

    return goals
      .map((goal) => ({
        id: String(goal.goal_id || goal._id || ""),
        title:
          goal.goalDetails?.goal_title ||
          goal.goal_title ||
          goal.title ||
          goal.name ||
          "Untitled Activity",
      }))
      .filter((goal) => goal.id);
  }, [circleData?.goals]);

  useEffect(() => {
    setChallengeAdId((currentId) => {
      const stillExists = circleActivities.some(
        (activity) => activity.id === currentId,
      );

      return stillExists ? currentId : circleActivities[0]?.id || "";
    });
  }, [circleActivities]);

  useEffect(() => {
    if (!token) {
      setLeaderboardUnavailable(true);
      setLoading(false);
      return;
    }

    loadCircle();

    const interval = setInterval(
      () => {
        loadCircle(true);
      },
      refreshIntervalMinutes * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [token, refreshIntervalMinutes]);

  const showJoinedUserToast = (member) => {
    if (!member) return;

    if (joinToastTimerRef.current) {
      clearTimeout(joinToastTimerRef.current);
    }

    const nickname = member.nickname || member.name || "New member";

    setJoinedUserToast({
      nickname,
      image: member.image
        ? member.image.startsWith("http")
          ? member.image
          : process.env.REACT_APP_IMG_FOLDER + member.image
        : null,
    });

    joinToastTimerRef.current = setTimeout(() => {
      setJoinedUserToast(null);
    }, 5000);
  };

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    let timer;

    setPublicMusic(null);

    const loadMusic = async () => {
      try {
        const { data } = await axios.get(
          process.env.REACT_APP_API_URL +
            `circles/leaderboard/public/${encodeURIComponent(token)}/music`,
          { timeout: 10000 },
        );

        if (!cancelled) setPublicMusic(data);
      } catch (error) {
        if (!cancelled) {
          console.warn("Music refresh failed; keeping last data:", {
            status: error.response?.status,
            message: error.message,
          });
        }
      } finally {
        if (!cancelled) timer = setTimeout(loadMusic, 10000);
      }
    };

    loadMusic();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token]);

  useEffect(() => {
    return () => {
      if (joinToastTimerRef.current) {
        clearTimeout(joinToastTimerRef.current);
      }

      if (scoreUpdateTimerRef.current) {
        clearTimeout(scoreUpdateTimerRef.current);
      }
    };
  }, []);

  const updateVisibleMetric = (key, checked) => {
    setSettingsError("");

    setVisibleMetrics((current) => {
      const next = {
        ...current,
        [key]: checked,
      };

      const hasAtLeastOneVisibleMetric =
        (circleData?.showSteps === true && next.steps) ||
        next.distance ||
        next.points ||
        next.checkins;

      if (!hasAtLeastOneVisibleMetric) {
        setSettingsError(
          circleData?.showSteps
            ? "At least one metric must remain visible."
            : "At least one metric other than Steps must remain visible.",
        );

        return current;
      }
      setSettingsChanged(true);
      return next;
    });
  };

  const normalizeLeaderboardSettings = (settings = {}) => {
    const refreshInterval = Number(settings.refreshIntervalMinutes);

    return {
      autoRotate: settings.autoRotate !== false,

      showWeeklyWinners: settings.showWeeklyWinners !== false,

      competitionCircleIds: Array.isArray(settings.competitionCircleIds)
        ? [...new Set(settings.competitionCircleIds.map(String))]
        : [],

      weeklyWinnersIntervalMinutes: [1, 2, 5, 10, 15].includes(
        Number(settings.weeklyWinnersIntervalMinutes),
      )
        ? Number(settings.weeklyWinnersIntervalMinutes)
        : 2,

      showAllTimeWinners: settings.showAllTimeWinners !== false,

      allTimeWinnersIntervalMinutes: [1, 2, 5, 10, 15].includes(
        Number(settings.allTimeWinnersIntervalMinutes),
      )
        ? Number(settings.allTimeWinnersIntervalMinutes)
        : 5,

      refreshIntervalMinutes: [1, 2, 5, 10, 15].includes(refreshInterval)
        ? refreshInterval
        : 2,

      topLeadersCount: [1, 2, 3, 4, 5].includes(
        Number(settings.topLeadersCount),
      )
        ? Number(settings.topLeadersCount)
        : 3,

      rotatingRowsCount: [3, 4, 5, 6, 7, 8, 9, 10].includes(
        Number(settings.rotatingRowsCount),
      )
        ? Number(settings.rotatingRowsCount)
        : 7,

      memberRotationSeconds: [0, 5, 10, 15, 20, 30, 45, 60].includes(
        Number(settings.memberRotationSeconds),
      )
        ? Number(settings.memberRotationSeconds)
        : 20,

      showQR: settings.showQR !== false,

      excludeAdmin: settings.excludeAdmin !== false,

      rtlMode: settings.rtlMode === true,

      displayScale: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100].includes(
        Number(settings.displayScale),
      )
        ? Number(settings.displayScale)
        : 100,

      selectedActivityId: settings.selectedActivityId || "all",

      notesTimeFrame: settings.notesTimeFrame || "30",

      fromDate: settings.fromDate || "",

      toDate: settings.toDate || "",
      startDate: settings.startDate || "",
      startTime: settings.startTime || "",

      visibleMetrics: {
        steps: settings.visibleMetrics?.steps !== false,

        distance: settings.visibleMetrics?.distance !== false,

        points: settings.visibleMetrics?.points !== false,

        checkins: settings.visibleMetrics?.checkins !== false,
      },
    };
  };

  const applyLeaderboardSettings = (settings) => {
    const normalized = normalizeLeaderboardSettings(settings);

    setAutoRotate(normalized.autoRotate);

    setCompetitionCircleIdsText(normalized.competitionCircleIds.join(", "));

    setRefreshIntervalMinutes(normalized.refreshIntervalMinutes);

    setTopLeadersCount(normalized.topLeadersCount);
    setRotatingRowsCount(normalized.rotatingRowsCount);
    setMemberRotationSeconds(normalized.memberRotationSeconds);

    setShowQR(normalized.showQR);

    setExcludeAdmin(normalized.excludeAdmin);

    setRtlMode(normalized.rtlMode);

    setDisplayScale(normalized.displayScale);

    setSelectedActivityId(normalized.selectedActivityId);

    setNotesTimeFrame(normalized.notesTimeFrame);

    setFromDate(normalized.fromDate);
    setToDate(normalized.toDate);
    setStartDate(normalized.startDate);
    setStartTime(normalized.startTime || "09:00");

    setVisibleMetrics(normalized.visibleMetrics);

    setShowWeeklyWinners(normalized.showWeeklyWinners);
    setWeeklyWinnersIntervalMinutes(normalized.weeklyWinnersIntervalMinutes);

    setShowAllTimeWinners(normalized.showAllTimeWinners);
    setAllTimeWinnersIntervalMinutes(normalized.allTimeWinnersIntervalMinutes);

    return normalized;
  };

  const closeSettingsModal = () => {
    /*
     * Restore DB values and discard unsaved edits.
     */
    applyLeaderboardSettings(circleData?.leaderboardSettings);

    settingsModalOpenRef.current = false;
    setSettingsError("");
    setShowSettingsModal(false);
    setAutoFit(readSavedAutoFit());
  };

  const openSettingsModal = () => {
    applyLeaderboardSettings(circleData?.leaderboardSettings);

    setSettingsError("");
    setSettingsChanged(false);
    settingsModalOpenRef.current = true;
    setShowSettingsModal(true);
    setAutoFit(readSavedAutoFit());
  };

  useEffect(() => {
    // Wait until the circle and its settings finish loading
    if (!circleData) return;
    if (!metricOrder.length) return;

    // Keep Steps when it is available.
    // Otherwise use the first available metric.
    if (!metricOrder.includes(metric)) {
      setMetric(metricOrder[0]);
    }
  }, [circleData, metricOrder, metric]);

  useEffect(() => {
    if (!autoRotate) return;
    if (!metricOrder.length) return;

    const interval = setInterval(() => {
      setMetric((currentMetric) => {
        const currentIndex = metricOrder.indexOf(currentMetric);

        const nextIndex =
          currentIndex === -1 || currentIndex === metricOrder.length - 1
            ? 0
            : currentIndex + 1;

        return metricOrder[nextIndex];
      });
    }, 300000);

    return () => clearInterval(interval);
  }, [autoRotate, metricOrder]);

  const loadCircle = async (backgroundRefresh = false) => {
    try {
      if (!backgroundRefresh) {
        setLoading(true);
      }

      /*
       * First load the Circle and its DB settings.
       */
      const circleRes = await axios.get(
        process.env.REACT_APP_API_URL + "circles/leaderboard/public/" + token,
      );

      const circle = circleRes.data.circle;

      setOwnedCircles(
        Array.isArray(circleRes.data.ownedCircles)
          ? circleRes.data.ownedCircles
          : [],
      );

      const baseLeaderboard = circleRes.data.leaderboard || [];

      const dbSettings = normalizeLeaderboardSettings(
        circle.leaderboardSettings || circleRes.data.leaderboardSettings,
      );

      /*
       * Keep the normalized DB settings on circleData.
       */
      circle.leaderboardSettings = dbSettings;

      /*
       * Do not overwrite modal controls during editing.
       */
      if (!settingsModalOpenRef.current) {
        applyLeaderboardSettings(dbSettings);
      }

      const activeTimeFrame = dbSettings.notesTimeFrame;

      const activeFromDate = dbSettings.fromDate;

      const activeToDate = dbSettings.toDate;

      const isCustomDateRange = activeTimeFrame === "custom";

      const hasValidCustomDateRange =
        isCustomDateRange &&
        activeFromDate &&
        activeToDate &&
        activeFromDate <= activeToDate;

      const dateRangePayload = hasValidCustomDateRange
        ? {
            fromDate: activeFromDate,
            toDate: activeToDate,
          }
        : {};

      const circleId = circle._id;

      /*
       * Detect newly joined members.
       */
      const currentMemberIds = baseLeaderboard
        .map((member) => String(member.uid || member._id || ""))
        .filter(Boolean);

      if (previousMemberIdsRef.current !== null) {
        const newMembers = baseLeaderboard.filter((member) => {
          const memberId = String(member.uid || member._id || "");

          return memberId && !previousMemberIdsRef.current.includes(memberId);
        });

        if (newMembers.length > 0) {
          showJoinedUserToast(newMembers[0]);
        }
      }

      previousMemberIdsRef.current = currentMemberIds;

      /*
       * Load steps using the DB time-frame settings.
       */
      let stepsRows = [];

      try {
        const stepsRes = await axios.post(
          process.env.REACT_APP_API_URL + "logs/steps/leaderboard",
          {
            deviceUID: "eabc2583-cd2d-4b18-bcdc-abd890f7acc3",
            leaderboardToken: token,
            numOfDays:
              activeTimeFrame === "all" || isCustomDateRange
                ? 0
                : Number(activeTimeFrame),

            ...dateRangePayload,

            limit: 200,
          },
        );

        if (stepsRes.data.success) {
          stepsRows = stepsRes.data.leaderboard || [];
        }
      } catch (stepsError) {
        console.log("Steps leaderboard load error:", stepsError);
      }

      /*
       * Load notes using the DB time-frame settings.
       */
      let notesRows = [];

      try {
        const notesRes = await axios.post(
          process.env.REACT_APP_API_URL + "notes/get-recent-notes-bycircleid",
          {
            circleId,
            deviceUID: "public",
            excludeOwn: false,
            limit: 300,

            numOfDays:
              activeTimeFrame === "all"
                ? 36500
                : isCustomDateRange
                  ? 0
                  : Number(activeTimeFrame),

            ...dateRangePayload,
          },
        );

        if (notesRes.data.success) {
          notesRows = notesRes.data.notes || [];
        }
      } catch (notesError) {
        console.log("Circle notes load error:", notesError);
      }

      const week = getLastCompletedWeek();

      const buildStepWinners = (rows, count) => {
        const scoresByUser = new Map();

        rows.forEach((row) => {
          const id =
            row.userId || row.uid || row._id || row.user_id || row.created_uid;

          const steps = Number(row.steps);

          if (id && Number.isFinite(steps) && steps > 0) {
            scoresByUser.set(String(id), steps);
          }
        });

        // Use the public leaderboard's member list for names and avatars.
        return baseLeaderboard
          .map((member) => {
            const id = String(member.uid || member._id || "");

            const image = member.image
              ? /^(https?:\/\/|data:|blob:)/i.test(member.image)
                ? member.image
                : process.env.REACT_APP_IMG_FOLDER + member.image
              : DEFAULT_AVATAR;

            return {
              id,
              nickname: member.nickname || member.name || "Member",
              image,
              steps: scoresByUser.get(id) ?? 0,
              circleId: member.circleId,
              circleName: member.circleName,
              circleColor: member.circleColor,
            };
          })
          .filter((member) => member.id && member.steps > 0)
          .sort((a, b) => b.steps - a.steps || a.id.localeCompare(b.id))
          .slice(0, count);
      };

      if (circle.showSteps === true) {
        const requestWinners = async (datePayload) => {
          const response = await axios.post(
            process.env.REACT_APP_API_URL + "logs/steps/leaderboard",
            {
              deviceUID: "eabc2583-cd2d-4b18-bcdc-abd890f7acc3",
              leaderboardToken: token,
              numOfDays: 0,
              ...datePayload,
              limit: 200,
            },
          );

          if (
            !response.data.success ||
            !Array.isArray(response.data.leaderboard)
          ) {
            throw new Error("Invalid steps winners response");
          }

          return response.data.leaderboard;
        };

        const results = await Promise.allSettled([
          requestWinners(week),
          requestWinners({}),
        ]);

        results.forEach((result) => {
          if (result.status === "rejected") {
            console.log("Steps winners load error:", result.reason);
          }
        });

        setWinnerData({
          weekly:
            results[0].status === "fulfilled"
              ? buildStepWinners(results[0].value, WINNER_CONFIG.weekly.count)
              : [],
          allTime:
            results[1].status === "fulfilled"
              ? buildStepWinners(results[1].value, WINNER_CONFIG.allTime.count)
              : [],
          week,
        });
      } else {
        setWinnerData({ weekly: [], allTime: [], week: null });
      }

      setCircleData(circle);
      setLeaderboard(baseLeaderboard);
      setStepsLeaderboard(stepsRows);
      setCircleNotes(notesRows);

      setLeaderboardUnavailable(false);
    } catch (error) {
      console.log("Circle leaderboard load error:", error);

      setLeaderboardUnavailable(true);
    } finally {
      if (!backgroundRefresh) {
        setLoading(false);
      }
    }
  };

  const handleAdminSignOut = () => {
    localStorage.removeItem("jwtToken");

    // Clear any default Authorization header.
    delete axios.defaults.headers.common.Authorization;

    setCanManageSettings(false);
    setCheckingAdmin(false);
    setAdminEmail("");
    setAdminPassword("");
    setAdminLoginError("");
    setSettingsError("");

    // Discard unsaved settings.
    applyLeaderboardSettings(circleData?.leaderboardSettings);
    setAutoFit(readSavedAutoFit());
    setSettingsChanged(false);
  };

  const handleAdminLogin = async (event) => {
    event.preventDefault();

    if (adminSigningIn || !circleData?._id) return;

    setAdminSigningIn(true);
    setAdminLoginError("");

    try {
      const { data: loginData } = await axios.post(
        process.env.REACT_APP_API_URL + "users/login",
        {
          email: adminEmail.trim().toLowerCase(),
          password: adminPassword,
        },
      );

      const loginToken = loginData.token;

      if (typeof loginToken !== "string" || !loginToken.trim()) {
        throw new Error("Missing login token");
      }

      const { data } = await axios.get(
        process.env.REACT_APP_API_URL + "users/admin/circles",
        {
          headers: { Authorization: loginToken },
        },
      );

      const allowed = (data.circles || []).some(
        (circle) => String(circle._id) === String(circleData._id),
      );

      if (!allowed) {
        setAdminLoginError(
          "This account does not have admin access to this circle.",
        );
        return;
      }

      localStorage.setItem("jwtToken", loginToken);
      setCanManageSettings(true);
    } catch (error) {
      const data = error.response?.data;

      setAdminLoginError(
        data?.message ||
          data?.emailnotfound ||
          data?.passwordincorrect ||
          data?.email ||
          data?.password ||
          "Unable to sign in. Please check your details and try again.",
      );
    } finally {
      setAdminPassword("");
      setAdminSigningIn(false);
    }
  };

  const saveLeaderboardSettings = async () => {
    if (!canManageSettings || !localStorage.getItem("jwtToken")) {
      setSettingsError("Please sign in as an admin of this circle.");
      return;
    }

    if (
      startDate &&
      !Number.isFinite(getIsraelLaunchTimestamp(startDate, startTime))
    ) {
      setSettingsError("Please select a valid launch date and Israel time.");
      return;
    }

    if (hasInvalidDateRange) {
      setSettingsError("Please select a valid start and end date.");

      return;
    }

    const hasVisibleMetric =
      (circleData?.showSteps === true && visibleMetrics.steps) ||
      visibleMetrics.distance ||
      visibleMetrics.points ||
      visibleMetrics.checkins;

    if (!hasVisibleMetric) {
      setSettingsError(
        circleData?.showSteps
          ? "At least one metric must remain visible."
          : "At least one metric other than Steps must remain visible.",
      );

      return;
    }

    try {
      setSavingSettings(true);
      setSettingsError("");

      const competitionCircleIds = [
        ...new Set(
          competitionCircleIdsText
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean),
        ),
      ].filter((id) => id !== String(circleData?._id));

      const settingsPayload = {
        startDate: startDate || null,
        startTime: startDate ? startTime : null,
        autoRotate,
        competitionCircleIds,
        showWeeklyWinners,
        weeklyWinnersIntervalMinutes,
        showAllTimeWinners,
        allTimeWinnersIntervalMinutes,
        refreshIntervalMinutes,

        topLeadersCount,
        rotatingRowsCount,
        memberRotationSeconds,

        showQR,

        excludeAdmin,

        rtlMode,
        displayScale,
        selectedActivityId: selectedActivityId || "all",

        notesTimeFrame,

        fromDate: notesTimeFrame === "custom" ? fromDate : "",

        toDate: notesTimeFrame === "custom" ? toDate : "",

        visibleMetrics: {
          steps: visibleMetrics.steps,
          distance: visibleMetrics.distance,
          points: visibleMetrics.points,
          checkins: visibleMetrics.checkins,
        },
      };

      const response = await axios.patch(
        process.env.REACT_APP_API_URL +
          "circles/leaderboard/public/" +
          token +
          "/settings",
        {
          leaderboardSettings: settingsPayload,
        },
        {
          headers: {
            Authorization: localStorage.getItem("jwtToken") || "",
          },
        },
      );

      if (!response.data?.success) {
        throw new Error(
          response.data?.message || "Settings could not be saved",
        );
      }

      try {
        localStorage.setItem(autoFitStorageKey, String(autoFit));
      } catch {
        // The current session still uses the selected mode.
      }
      settingsModalOpenRef.current = false;
      setShowSettingsModal(false);
      setSettingsChanged(false);

      /*
       * Reload everything from MongoDB.
       */
      await loadCircle();
    } catch (error) {
      console.log("Save leaderboard settings error:", error);

      setSettingsError(
        error.response?.data?.message ||
          error.message ||
          "Could not save leaderboard settings.",
      );
    } finally {
      setSavingSettings(false);
    }
  };

  function getCircleStatsFromNotes(userId, notes = []) {
    let points = 0;
    let checkins = 0;
    let distance = 0;
    const imageBonusTracker = new Set();

    notes.forEach((note) => {
      if (
        selectedActivityId !== "all" &&
        String(
          note?.note_rel_goal_parent_id ||
            note?.note_rel_goal_id ||
            note?.goal_id ||
            note?.note_goal_id ||
            "",
        ) !== String(selectedActivityId)
      ) {
        return;
      }
      if (String(note?.note_type) !== "2") return;
      if (String(note?.created_uid) !== String(userId)) return;

      checkins += 1;

      const task = Array.isArray(note?.note_task) ? note.note_task[0] : null;
      const taskTotalScore = Number(task?.task_totalscore || 0);

      if (!Number.isNaN(taskTotalScore)) {
        points += Math.round(taskTotalScore * 100);
      }

      const dayKey = note?.created_date
        ? new Date(note.created_date).toISOString().slice(0, 10)
        : "unknown-day";

      const bonusKey = `${userId}_${note?.note_rel_task_id}_${dayKey}`;

      if (note?.note_image && !imageBonusTracker.has(bonusKey)) {
        points += 5;
        imageBonusTracker.add(bonusKey);
      }

      try {
        const parsed =
          typeof note?.note_value === "string"
            ? JSON.parse(note.note_value)
            : note?.note_value || {};

        const noteDistance = Number(parsed?.distance || 0);

        if (!Number.isNaN(noteDistance)) {
          distance += noteDistance;
        }
      } catch (e) {}
    });

    return {
      points,
      checkins,
      distance: Number(distance.toFixed(2)),
    };
  }

  const leaders = useMemo(() => {
    const members = leaderboard || [];

    return [...members]
      .map((member) => {
        const nickname = member.nickname || member.name || "Member";

        return {
          id: member.uid || member._id || nickname,
          nickname,

          circleId: member.circleId,
          circleName: member.circleName,
          circleColor: member.circleColor,

          image: member.image
            ? member.image.startsWith("http")
              ? member.image
              : process.env.REACT_APP_IMG_FOLDER + member.image
            : null,
          steps: (() => {
            const stepRow = stepsLeaderboard.find((row) => {
              const rowUserId =
                row.userId ||
                row.uid ||
                row._id ||
                row.user_id ||
                row.created_uid;

              return String(rowUserId) === String(member.uid);
            });

            return Number(
              stepRow?.steps || member.totalSteps || member.steps || 0,
            );
          })(),
          ...getCircleStatsFromNotes(member.uid, circleNotes),
        };
      })
      .map((member) => ({
        ...member,
        score:
          metric === "distance"
            ? member.distance
            : metric === "steps"
              ? member.steps
              : metric === "points"
                ? member.points
                : member.checkins,
      }))
      .filter((member) => Number(member.score) > 0)
      .sort((a, b) => b.score - a.score);
  }, [leaderboard, stepsLeaderboard, circleNotes, metric, selectedActivityId]);

  const topLeaders = useMemo(() => {
    return leaders.slice(0, topLeadersCount);
  }, [leaders, topLeadersCount]);

  const rotatingLeaders = useMemo(() => {
    return leaders.slice(topLeadersCount);
  }, [leaders, topLeadersCount]);

  const totalMemberPages = Math.max(
    1,
    Math.ceil(rotatingLeaders.length / rotatingRowsCount),
  );

  const visibleRotatingLeaders = useMemo(() => {
    const start = memberPage * rotatingRowsCount;

    return rotatingLeaders.slice(start, start + rotatingRowsCount);
  }, [rotatingLeaders, memberPage, rotatingRowsCount]);

  useEffect(() => {
    // 0 means member rotation is disabled.
    if (memberRotationSeconds === 0) {
      setMemberPage(0);
      return;
    }

    if (totalMemberPages <= 1) {
      setMemberPage(0);
      return;
    }

    const interval = setInterval(() => {
      setMemberPage((currentPage) => {
        return (currentPage + 1) % totalMemberPages;
      });
    }, memberRotationSeconds * 1000);

    return () => clearInterval(interval);
  }, [totalMemberPages, memberRotationSeconds]);

  useEffect(() => {
    if (memberPage >= totalMemberPages) {
      setMemberPage(0);
    }
  }, [memberPage, totalMemberPages]);

  useEffect(() => {
    setMemberPage(0);
  }, [metric, selectedActivityId, notesTimeFrame, fromDate, toDate]);

  useEffect(() => {
    if (!leaders.length) return;

    const nextScores = new Map(previousScoresRef.current);
    const detectedUpdates = {};

    leaders.forEach((member) => {
      const scoreKey = `${metric}:${member.id}`;
      const currentScore = Number(member.score) || 0;
      const previousScore = previousScoresRef.current.get(scoreKey);

      if (previousScore !== undefined && currentScore > previousScore) {
        detectedUpdates[member.id] = currentScore - previousScore;
      }

      nextScores.set(scoreKey, currentScore);
    });

    previousScoresRef.current = nextScores;

    if (Object.keys(detectedUpdates).length > 0) {
      setScoreUpdates(detectedUpdates);

      if (scoreUpdateTimerRef.current) {
        clearTimeout(scoreUpdateTimerRef.current);
      }

      scoreUpdateTimerRef.current = setTimeout(() => {
        setScoreUpdates({});
      }, 4000);
    }
  }, [leaders, metric]);

  const formatScore = (score) => {
    const numericScore = Number(score) || 0;

    if (metric === "distance") {
      return `${numericScore.toFixed(2)} km`;
    }

    const roundedScore = Math.round(numericScore);

    if (metric === "steps") {
      return `${roundedScore.toLocaleString()} steps`;
    }

    if (metric === "points") {
      return `${roundedScore.toLocaleString()} pts`;
    }

    return `${roundedScore.toLocaleString()} check-ins`;
  };

  const formatScoreNumber = (score) => {
    const numericScore = Number(score) || 0;
    const locale = rtlMode ? "he-IL" : "en-US";

    return numericScore.toLocaleString(locale, {
      minimumFractionDigits: metric === "distance" ? 2 : 0,
      maximumFractionDigits: metric === "distance" ? 2 : 0,
    });
  };

  const scoreMetricLabel =
    metric === "steps"
      ? rtlMode
        ? "צעדים"
        : "steps"
      : metric === "distance"
        ? rtlMode
          ? "ק״מ"
          : "km"
        : metric === "points"
          ? rtlMode
            ? "נקודות"
            : "points"
          : rtlMode
            ? "ביצועים"
            : "check-ins";
  const heroImage = circleData?.circle_image
    ? process.env.REACT_APP_IMG_FOLDER + circleData.circle_image
    : "/images/circle-placeholder.jpg";

  const circleLogoUrl = circleData?.circle_logo
    ? `${
        circleData.circle_logo.startsWith("http")
          ? circleData.circle_logo
          : process.env.REACT_APP_IMG_FOLDER + circleData.circle_logo
      }?v=${encodeURIComponent(circleData.updatedAt || "2")}`
    : "";

  const winnerRuntimeRef = useRef(null);

  winnerRuntimeRef.current = {
    winnerData,
    showSettingsModal,
    metric,
  };

  const winnersEnabled =
    Boolean(token && circleData?._id) &&
    circleData?.showSteps === true &&
    visibleMetrics.steps &&
    !isComingSoon &&
    !leaderboardUnavailable;

  const savedShowWeeklyWinners =
    circleData?.leaderboardSettings?.showWeeklyWinners !== false;

  const savedWeeklyInterval =
    circleData?.leaderboardSettings?.weeklyWinnersIntervalMinutes ?? 2;

  const savedShowAllTimeWinners =
    circleData?.leaderboardSettings?.showAllTimeWinners !== false;

  const savedAllTimeInterval =
    circleData?.leaderboardSettings?.allTimeWinnersIntervalMinutes ?? 5;

  useEffect(() => {
    setActiveWinnerModal(null);

    if (
      !winnersEnabled ||
      (!savedShowWeeklyWinners && !savedShowAllTimeWinners)
    ) {
      return;
    }

    const weeklyIntervalMs = savedWeeklyInterval * 60_000;
    const allTimeIntervalMs = savedAllTimeInterval * 60_000;

    let nextWeekly = Date.now() + weeklyIntervalMs;
    let nextAllTime = Date.now() + allTimeIntervalMs;
    let occupiedUntil = 0;

    const timer = setInterval(() => {
      const now = Date.now();
      const current = winnerRuntimeRef.current;

      if (
        document.hidden ||
        current.showSettingsModal ||
        current.metric !== "steps" ||
        now < occupiedUntil
      ) {
        return;
      }

      const weeklyDue = savedShowWeeklyWinners && now >= nextWeekly;

      const allTimeDue = savedShowAllTimeWinners && now >= nextAllTime;

      if (!weeklyDue && !allTimeDue) return;

      const type =
        weeklyDue && (!allTimeDue || nextWeekly <= nextAllTime)
          ? "weekly"
          : "allTime";

      if (type === "weekly") {
        nextWeekly = now + weeklyIntervalMs;
      } else {
        nextAllTime = now + allTimeIntervalMs;
      }

      const users = current.winnerData[type];
      if (!users.length) return;

      setActiveWinnerModal({
        type,
        users,
        week: current.winnerData.week,
      });

      occupiedUntil = now + WINNER_CONFIG.durationSeconds * 1000 + 3000;
    }, 1000);

    return () => clearInterval(timer);
  }, [
    token,
    winnersEnabled,
    savedShowWeeklyWinners,
    savedWeeklyInterval,
    savedShowAllTimeWinners,
    savedAllTimeInterval,
  ]);

  useEffect(() => {
    setWinnerModalClosing(false);

    if (!activeWinnerModal) return;

    const durationMs = WINNER_CONFIG.durationSeconds * 1000;

    const exitTimer = setTimeout(
      () => {
        setWinnerModalClosing(true);
      },
      Math.max(0, durationMs - 400),
    );

    const closeTimer = setTimeout(() => {
      setActiveWinnerModal(null);
    }, durationMs);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(closeTimer);
    };
  }, [activeWinnerModal]);

  useEffect(() => {
    if (!winnerModalClosing || !activeWinnerModal) return;

    const timer = setTimeout(() => {
      setActiveWinnerModal(null);
    }, 400);

    return () => clearTimeout(timer);
  }, [winnerModalClosing, activeWinnerModal]);

  useEffect(() => {
    if (showSettingsModal || metric !== "steps") {
      setActiveWinnerModal(null);
    }
  }, [showSettingsModal, metric]);

  if (leaderboardUnavailable) {
    return (
      <Page>
        <Helmet>
          <title>Circle Unavailable | Bealyo</title>
        </Helmet>
        <FitToScreen automatic={autoFit} manualScale={displayScale}>
          <Card
            ref={cardRef}
            style={{
              ...(isComingSoon && {
                minHeight: `calc(80vh / ${displayScale / 100})`,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
              }),
            }}
          >
            <div>
              <Logo
                src="/logo.png"
                alt="Bealyo"
                style={{ marginBottom: "30px" }}
              />

              <div
                style={{
                  fontSize: "52px",
                  marginBottom: "20px",
                }}
              >
                🏆
              </div>

              <h1
                style={{
                  color: "#fff",
                  fontSize: "28px",
                  marginBottom: "12px",
                }}
              >
                This Bealyo Circle is not available
              </h1>

              <p
                style={{
                  color: "#94a3b8",
                  fontSize: "16px",
                  lineHeight: "1.6",
                  maxWidth: "460px",
                  margin: "0 auto",
                }}
              >
                This leaderboard link may have expired or been disabled by the
                Circle admin.
              </p>
            </div>
          </Card>
        </FitToScreen>
        <Copyright style={{ marginTop: 6 }}>
          Powered by Bealyo • © {new Date().getFullYear()} Bealyo. All rights
          reserved.
        </Copyright>
      </Page>
    );
  }

  const formatDisplayDate = (value) => {
    if (!value) return "";

    return new Date(`${value}T00:00:00`).toLocaleDateString();
  };

  const timeFrameLabel =
    notesTimeFrame === "1"
      ? rtlMode
        ? "היום"
        : "Today"
      : ["7", "30", "60", "90"].includes(notesTimeFrame)
        ? rtlMode
          ? `${notesTimeFrame} הימים האחרונים`
          : `Last ${notesTimeFrame} days`
        : notesTimeFrame === "custom"
          ? fromDate && toDate
            ? `${formatDisplayDate(fromDate)} – ${formatDisplayDate(toDate)}`
            : rtlMode
              ? "טווח תאריכים"
              : "Custom dates"
          : rtlMode
            ? "כל הזמנים"
            : "All time";

  const leaderboardTitle = {
    steps: rtlMode ? "אלופי הצעדים 🏆" : "STEP LEADERBOARD",
    distance: rtlMode ? "הולכים רחוק" : "GOING THE DISTANCE",
    points: rtlMode ? "צוברים הצלחות" : "POINTS LEADERS",
    checkins: rtlMode ? "מובילים בהתמדה" : "CONSISTENCY LEADERS",
  }[metric];

  const displayedLeaders = [
    ...topLeaders.map((member, index) => ({
      ...member,
      actualRank: index + 1,

      isFixedLeader: true,

      // Only #1, #2, #3 get podium sizing/text treatment.
      isTopThree: index < 3,

      // True only for the final fixed leader.
      isLastFixedLeader: index === topLeaders.length - 1,
    })),

    ...visibleRotatingLeaders.map((member, index) => ({
      ...member,
      actualRank: topLeadersCount + memberPage * rotatingRowsCount + index + 1,

      isFixedLeader: false,
      isTopThree: false,
      isLastFixedLeader: false,
    })),
  ];

  // Reserve the configured number of rotating rows.
  // Empty slots keep the music ribbon in the same position.
  const emptyRowCount =
    leaders.length > 0
      ? Math.max(
          0,
          topLeaders.length + rotatingRowsCount - displayedLeaders.length,
        )
      : 0;
  return (
    <Page dir={rtlMode ? "rtl" : "ltr"} rtl={rtlMode}>
      <Helmet>
        <title>Circle Leaderboard | Bealyo</title>
      </Helmet>
      <FitToScreen automatic={autoFit} manualScale={displayScale}>
        <Card
          ref={cardRef}
          style={{
            ...(isComingSoon && {
              border: "none",
              minHeight: "760px",
              height: "auto",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",

              backgroundImage: `
  linear-gradient(
    to bottom,
    rgba(10, 16, 26, 0.82) 0%,
    rgba(10, 16, 26, 0.88) 50%,
    rgba(10, 16, 26, 0.95) 100%
  ),
  url("${heroImage}")
`,
              backgroundSize: "cover",
              backgroundPosition: "right center",
              backgroundRepeat: "no-repeat",
            }),
          }}
        >
          {isComingSoon ? (
            <LaunchScreen>
              <SettingsButton
                type="button"
                onClick={openSettingsModal}
                title="Leaderboard settings"
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                }}
              >
                ⚙
              </SettingsButton>

              <LaunchGroup>
                {circleLogoUrl ? (
                  <CircleLogoContainer>
                    <CircleLogo
                      src={circleLogoUrl}
                      alt={`${circleData?.name || "Circle"} logo`}
                    />
                  </CircleLogoContainer>
                ) : (
                  <Logo src="/logo.png" alt="Bealyo" />
                )}

                <LaunchName>{circleData?.name || "Circle"}</LaunchName>
              </LaunchGroup>

              <LaunchGroup>
                <LaunchLabel>
                  {rtlMode
                    ? "חוויה חדשה מתחילה בעוד"
                    : "A new experience starts in"}
                </LaunchLabel>
                <LaunchCountdown>
                  <CountdownUnit>
                    <CountdownNumber>{countdownDays}</CountdownNumber>
                    <CountdownUnitLabel>
                      {rtlMode ? "ימים" : "DAYS"}
                    </CountdownUnitLabel>
                  </CountdownUnit>

                  <CountdownSeparator>:</CountdownSeparator>

                  <CountdownUnit>
                    <CountdownNumber>{countdownHours}</CountdownNumber>
                    <CountdownUnitLabel>
                      {rtlMode ? "שעות" : "HOURS"}
                    </CountdownUnitLabel>
                  </CountdownUnit>

                  <CountdownSeparator>:</CountdownSeparator>

                  <CountdownUnit>
                    <CountdownNumber>{countdownMinutes}</CountdownNumber>
                    <CountdownUnitLabel>
                      {rtlMode ? "דקות" : "MINUTES"}
                    </CountdownUnitLabel>
                  </CountdownUnit>

                  <CountdownSeparator>:</CountdownSeparator>

                  <CountdownUnit>
                    <CountdownNumber>{countdownSeconds}</CountdownNumber>
                    <CountdownUnitLabel>
                      {rtlMode ? "שניות" : "SECONDS"}
                    </CountdownUnitLabel>
                  </CountdownUnit>
                </LaunchCountdown>
              </LaunchGroup>

              <LaunchGroup>
                {circleId && (
                  <>
                    <LaunchQR>
                      <QRCodeCanvas
                        value={`https://www.bealyo.com/circle/${circleId}`}
                        size={220}
                        bgColor="#ffffff"
                        fgColor="#000000"
                        level="M"
                        marginSize={2}
                        style={{
                          display: "block",
                          maxWidth: "100%",
                          height: "auto",
                        }}
                      />
                    </LaunchQR>

                    <LaunchInvite>
                      {rtlMode
                        ? "סרקו והצטרפו לחוויה!"
                        : "Scan to be part of it!"}
                    </LaunchInvite>
                  </>
                )}

                <PoweredBy>
                  <PoweredByText>Powered by</PoweredByText>
                  <PoweredByLogo src="/logo.png" alt="Bealyo" />
                </PoweredBy>
              </LaunchGroup>
            </LaunchScreen>
          ) : (
            <>
              <Hero
                style={{
                  backgroundImage: `url(${heroImage})`,
                }}
              >
                <HeroOverlay />

                <HeroContent>
                  <HeroTopRow>
                    {circleLogoUrl ? (
                      <CircleLogoContainer>
                        <CircleLogo
                          src={circleLogoUrl}
                          alt={`${circleData?.name || "Circle"} logo`}
                        />
                      </CircleLogoContainer>
                    ) : (
                      <Logo src="/logo.png" alt="Bealyo" />
                    )}

                    <TopRightButtons>
                      <SettingsButton
                        type="button"
                        onClick={openSettingsModal}
                        title="Leaderboard settings"
                      >
                        ⚙
                      </SettingsButton>
                    </TopRightButtons>
                  </HeroTopRow>

                  <HeroBottom rtl={rtlMode}>
                    <HeroTitle>
                      {circleData?.name ||
                        (rtlMode ? "לוח מובילים" : "Circle Leaderboard")}
                    </HeroTitle>

                    <HeroSubtitle>
                      {rtlMode
                        ? "המשתמשים המובילים בקהילה"
                        : "Top members in this circle"}
                    </HeroSubtitle>
                    <HeroStats>
                      {leaders.length === 0
                        ? rtlMode
                          ? "אין חברים פעילים"
                          : "No active members"
                        : leaders.length === 1
                          ? rtlMode
                            ? "חבר פעיל אחד"
                            : "1 active member"
                          : rtlMode
                            ? `${leaders.length} חברים פעילים`
                            : `${leaders.length} active members`}{" "}
                      •{" "}
                      {selectedActivityId === "all"
                        ? "All activities"
                        : circleActivities.find(
                            (a) => a.id === selectedActivityId,
                          )?.title || "Selected activity"}{" "}
                      • {timeFrameLabel} •{" "}
                      <LiveIndicator>
                        <LiveDot />
                        Live
                      </LiveIndicator>
                    </HeroStats>
                  </HeroBottom>

                  {showQR && circleId && (
                    <HeroQRBlock>
                      <HeroQR>
                        <QRCodeSVG
                          value={`https://www.bealyo.com/circle/${circleId}`}
                          size={180}
                          bgColor="#ffffff"
                          fgColor="#000000"
                          level="M"
                          marginSize={4}
                        />

                        <QRText>Scan to Join</QRText>
                      </HeroQR>

                      <PoweredBy>
                        <PoweredByText>Powered by</PoweredByText>
                        <PoweredByLogo src="/logo.png" alt="Bealyo" />
                      </PoweredBy>
                    </HeroQRBlock>
                  )}
                </HeroContent>
              </Hero>
              <LeaderboardHeading>
                <LeaderboardTitle>{leaderboardTitle}</LeaderboardTitle>

                <LeaderboardSubtitle>
                  <bdi>{timeFrameLabel}</bdi>
                  {!loading && (
                    <>
                      {" • "}
                      {leaders.length.toLocaleString(
                        rtlMode ? "he-IL" : "en-US",
                      )}{" "}
                      {rtlMode ? "משתתפים בדירוג" : "ranked participants"}
                    </>
                  )}
                </LeaderboardSubtitle>
              </LeaderboardHeading>
              {metricOrder.length > 1 && (
                <TabsRow>
                  <Tabs>
                    {circleData?.showSteps && visibleMetrics.steps && (
                      <Tab
                        active={metric === "steps"}
                        onClick={() => setMetric("steps")}
                      >
                        {rtlMode ? "צעדים" : "Steps"}
                      </Tab>
                    )}

                    {visibleMetrics.distance && (
                      <Tab
                        active={metric === "distance"}
                        onClick={() => setMetric("distance")}
                      >
                        {rtlMode ? "מרחק" : "Distance"}
                      </Tab>
                    )}

                    {visibleMetrics.points && (
                      <Tab
                        active={metric === "points"}
                        onClick={() => setMetric("points")}
                      >
                        {rtlMode ? "נקודות" : "Points"}
                      </Tab>
                    )}

                    {visibleMetrics.checkins && (
                      <Tab
                        active={metric === "checkins"}
                        onClick={() => setMetric("checkins")}
                      >
                        {rtlMode ? "ביצועים" : "Check-ins"}
                      </Tab>
                    )}
                  </Tabs>
                </TabsRow>
              )}

              {loading && <Empty>Loading leaderboard...</Empty>}

              {!loading && !leaders.length && (
                <Empty>
                  {metric === "steps"
                    ? "The leaderboard is ready - take the first step! 👟"
                    : metric === "distance"
                      ? "The leaderboard is ready - be the first to go the distance! 🏃"
                      : metric === "points"
                        ? "The leaderboard is ready - be the first to earn points! ⭐"
                        : "The leaderboard is ready - complete an activity to get started! 🔥"}
                </Empty>
              )}

              {!loading &&
                displayedLeaders.map((member, index) => (
                  <Row
                    $rtl={rtlMode}
                    key={`${metric}-${member.id}`}
                    index={index}
                    isFixedLeader={member.isFixedLeader}
                    isLastFixedLeader={member.isLastFixedLeader}
                    podiumRank={member.isFixedLeader ? member.actualRank : null}
                    updated={Boolean(scoreUpdates[member.id])}
                  >
                    <Rank $rank={member.actualRank}>{member.actualRank}</Rank>

                    <Avatar
                      src={member.image || DEFAULT_AVATAR}
                      alt=""
                      onError={(event) => {
                        if (
                          event.currentTarget.getAttribute("src") !==
                          DEFAULT_AVATAR
                        ) {
                          event.currentTarget.src = DEFAULT_AVATAR;
                        }
                      }}
                    />

                    <MemberInfo>
                      <Name title={member.nickname}>
                        <bdi dir="auto">{member.nickname}</bdi>
                      </Name>

                      {circleData?.name && (
                        <div>
                          <CircleTag
                            $color={
                              clubColors[
                                String(member.circleId || circleData?._id)
                              ]
                            }
                          >
                            {member.circleName || circleData.name}
                          </CircleTag>
                        </div>
                      )}
                    </MemberInfo>

                    <ScoreArea>
                      <div>
                        <Score $topThree={member.isTopThree}>
                          <AnimatedNumber
                            value={member.score}
                            formatValue={formatScoreNumber}
                          />
                        </Score>

                        <ScoreLabel dir={rtlMode ? "rtl" : "ltr"}>
                          {scoreMetricLabel}
                        </ScoreLabel>
                      </div>

                      {scoreUpdates[member.id] > 0 && (
                        <ScoreChange>
                          +{formatScore(scoreUpdates[member.id])}
                        </ScoreChange>
                      )}
                    </ScoreArea>
                  </Row>
                ))}
              {!loading &&
                Array.from({ length: emptyRowCount }, (_, index) => (
                  <Row
                    key={`empty-row-${index}`}
                    aria-hidden="true"
                    style={{
                      visibility: "hidden",
                      opacity: 0,
                      animation: "none",
                      pointerEvents: "none",
                    }}
                  />
                ))}
              {!loading && leaders.length > 0 && totalMemberPages > 1 && (
                <div
                  style={{
                    marginTop: "10px",
                    paddingTop: "8px",
                    borderTop: "1px solid rgba(255,255,255,0.06)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "7px",
                    color: "#94a3b8",
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  <div>
                    Members #
                    {topLeadersCount + memberPage * rotatingRowsCount + 1}
                    {"–"}
                    {Math.min(
                      topLeadersCount +
                        memberPage * rotatingRowsCount +
                        visibleRotatingLeaders.length,
                      leaders.length,
                    )}{" "}
                    • {leaders.length} active members
                    {memberRotationSeconds > 0
                      ? " • More members coming up"
                      : " • Rotation off"}
                  </div>

                  {memberRotationSeconds > 0 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      {Array.from({ length: totalMemberPages }).map(
                        (_, pageIndex) => (
                          <span
                            key={pageIndex}
                            style={{
                              width: pageIndex === memberPage ? "18px" : "6px",
                              height: "6px",
                              borderRadius: "999px",
                              background:
                                pageIndex === memberPage
                                  ? "rgba(56, 189, 248, 0.9)"
                                  : "rgba(255,255,255,0.18)",
                              transition: "all 0.3s ease",
                            }}
                          />
                        ),
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {!isComingSoon && publicMusic?.enabled && circleId && (
            <MusicRibbon>
              <MusicRibbonIcon aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18V5l12-2v13" />
                  <path d="M9 9l12-2" />
                  <ellipse cx="6" cy="18" rx="3" ry="3" />
                  <ellipse cx="18" cy="16" rx="3" ry="3" />
                </svg>
              </MusicRibbonIcon>

              <MusicRibbonInfo>
                <small>
                  {currentMusic
                    ? rtlMode
                      ? "מתנגן עכשיו"
                      : "NOW PLAYING"
                    : displayedMusic
                      ? rtlMode
                        ? "הבא בתור"
                        : "UP NEXT"
                      : rtlMode
                        ? "המוזיקה של הקהילה"
                        : "COMMUNITY MUSIC"}
                </small>

                <MusicSongRequester
                  user={displayedMusic?.requestedBy}
                  rtlMode={rtlMode}
                  songTitle={[displayedMusic?.title, displayedMusic?.artist]
                    .filter(Boolean)
                    .join(" - ")}
                />

                <p>
                  {rtlMode
                    ? "הקצב שלכם. האימון של כולם. סרקו ובקשו שיר."
                    : "Your music. Everyone’s momentum. Scan to request."}
                </p>
              </MusicRibbonInfo>

              <MusicRibbonQR>
                <span>
                  {rtlMode
                    ? "מה בא לכם לשמוע?"
                    : "What would you like to hear?"}
                </span>

                <a
                  href={`https://www.bealyo.com/circle/${circleId}?action=request-song`}
                  aria-label={rtlMode ? "בקשת שיר" : "Request a song"}
                >
                  <QRCodeSVG
                    value={`https://www.bealyo.com/circle/${circleId}?action=request-song`}
                    size={220}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="M"
                    marginSize={4}
                  />
                </a>
              </MusicRibbonQR>
            </MusicRibbon>
          )}
        </Card>
      </FitToScreen>
      <Copyright style={{ marginTop: 6 }}>
        Powered by Bealyo • © {new Date().getFullYear()} Bealyo. All rights
        reserved.
      </Copyright>

      {showSettingsModal && (
        <ModalOverlay onClick={closeSettingsModal}>
          <ModalCard
            $wide={canManageSettings}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalTitle>
              {rtlMode ? "הגדרות לוח הדירוג" : "Leaderboard Settings"}
            </ModalTitle>

            {canManageSettings && (
              <button
                type="button"
                onClick={handleAdminSignOut}
                disabled={savingSettings || adminSigningIn}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 14,
                  padding: 0,
                  border: "none",
                  background: "transparent",
                  color: "#94a3b8",
                  font: "inherit",
                  fontSize: 10,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {rtlMode ? "התנתקות" : "Sign out"}
              </button>
            )}

            {checkingAdmin ? (
              <p style={{ padding: 24, textAlign: "center" }}>
                {rtlMode ? "בודק הרשאות..." : "Checking admin access…"}
              </p>
            ) : !canManageSettings ? (
              <form
                onSubmit={handleAdminLogin}
                style={{
                  display: "grid",
                  gap: 14,
                  padding: "20px 8px",
                }}
              >
                <p style={{ margin: "0 0 6px", lineHeight: 1.6 }}>
                  {rtlMode
                    ? "התחברו עם חשבון מנהל המועדון כדי לשנות את ההגדרות."
                    : "Sign in with your club admin account to manage settings."}
                </p>

                <label htmlFor="leaderboard-admin-email">
                  {rtlMode ? "אימייל" : "Email"}
                </label>

                <input
                  id="leaderboard-admin-email"
                  type="email"
                  autoComplete="username"
                  value={adminEmail}
                  onChange={(event) => setAdminEmail(event.target.value)}
                  disabled={adminSigningIn}
                  required
                  dir="ltr"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: "1px solid #64748b",
                    background: "#111827",
                    color: "#fff",
                    font: "inherit",
                  }}
                />

                <label htmlFor="leaderboard-admin-password">
                  {rtlMode ? "סיסמה" : "Password"}
                </label>

                <input
                  id="leaderboard-admin-password"
                  type="password"
                  autoComplete="current-password"
                  value={adminPassword}
                  onChange={(event) => setAdminPassword(event.target.value)}
                  disabled={adminSigningIn}
                  required
                  dir="ltr"
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "12px 14px",
                    borderRadius: 10,
                    border: "1px solid #64748b",
                    background: "#111827",
                    color: "#fff",
                    font: "inherit",
                  }}
                />

                {adminLoginError && (
                  <p role="alert" style={{ margin: 0, color: "#fca5a5" }}>
                    {adminLoginError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={adminSigningIn || !circleData?._id}
                  style={{
                    padding: "12px 24px",
                    borderRadius: 10,
                    border: "none",
                    background: "#c8102e",
                    color: "#fff",
                    font: "inherit",
                    fontWeight: 700,
                    cursor: adminSigningIn ? "wait" : "pointer",
                    opacity: adminSigningIn ? 0.7 : 1,
                  }}
                >
                  {adminSigningIn
                    ? rtlMode
                      ? "מתחבר..."
                      : "Signing in…"
                    : rtlMode
                      ? "כניסת מנהלים"
                      : "Admin sign in"}
                </button>

                <button
                  type="button"
                  onClick={closeSettingsModal}
                  style={{
                    padding: 10,
                    background: "transparent",
                    border: "none",
                    color: "inherit",
                    font: "inherit",
                    cursor: "pointer",
                  }}
                >
                  {rtlMode ? "סגירה" : "Close"}
                </button>
              </form>
            ) : (
              <>
                <SettingsLayout>
                  <SettingColumn>
                    <DateField>
                      <span>
                        {rtlMode
                          ? "תאריך תחילת הדירוג"
                          : "Leaderboard start date"}
                      </span>

                      <DateInput
                        type="date"
                        value={startDate}
                        disabled={savingSettings}
                        onChange={(event) => {
                          setStartDate(event.target.value);
                          setSettingsChanged(true);
                        }}
                      />
                    </DateField>
                    <DateField style={{ marginTop: 10 }}>
                      <span>
                        {rtlMode
                          ? "שעת ההשקה — שעון ישראל"
                          : "Launch time — Israel"}
                      </span>

                      <DateInput
                        type="time"
                        step={60}
                        value={startTime}
                        disabled={savingSettings || !startDate}
                        onChange={(event) => {
                          setStartTime(event.target.value);
                          setSettingsChanged(true);
                        }}
                      />
                    </DateField>

                    <div
                      style={{
                        marginTop: 6,
                        fontSize: 11,
                        lineHeight: 1.5,
                        color: "#94a3b8",
                      }}
                    >
                      {rtlMode
                        ? "החוויה תיפתח בתאריך ובשעה שנבחרו, לפי שעון ישראל. הצעדים ייספרו עבור כל יום ההשקה. הנתונים הקיימים לא יימחקו."
                        : "The experience launches at the selected date and time in Israel. Steps count for the whole launch day. Existing records are kept."}
                    </div>
                  </SettingColumn>

                  {circleActivities.length > 0 && (
                    <SettingColumn>
                      <SettingLabel>
                        {rtlMode ? "פרסום אתגר לטלוויזיה" : "Challenge TV Ad"}
                      </SettingLabel>

                      <ActivitySelect
                        value={challengeAdId}
                        onChange={(event) =>
                          setChallengeAdId(event.target.value)
                        }
                        aria-label={
                          rtlMode
                            ? "בחירת אתגר לפרסום בטלוויזיה"
                            : "Select challenge for TV ad"
                        }
                      >
                        {circleActivities.map((activity) => (
                          <option key={activity.id} value={activity.id}>
                            {activity.title}
                          </option>
                        ))}
                      </ActivitySelect>

                      {challengeAdId && (
                        <ChallengeAdLink
                          href={`/challenge/${encodeURIComponent(
                            challengeAdId,
                          )}/tv?circleToken=${encodeURIComponent(token)}&rtl=${rtlMode ? "1" : "0"}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginTop: "10px" }}
                        >
                          {rtlMode ? "פתחו פרסום לטלוויזיה" : "Open TV Ad"}
                        </ChallengeAdLink>
                      )}
                    </SettingColumn>
                  )}

                  <SettingRow>
                    <span>Auto switch metrics every 5 minutes</span>

                    <Switch
                      type="checkbox"
                      checked={autoRotate}
                      onChange={(e) => {
                        setAutoRotate(e.target.checked);
                        setSettingsChanged(true);
                      }}
                    />
                  </SettingRow>

                  <SettingColumn>
                    <SettingLabel>
                      {rtlMode ? "תחרות בין קהילות" : "Community Competition"}
                    </SettingLabel>

                    <div
                      style={{
                        marginBottom: 8,
                        fontSize: 11,
                        color: "#94a3b8",
                        lineHeight: 1.5,
                      }}
                    >
                      {rtlMode
                        ? "בחרו קהילות נוספות להצגה בלוח המובילים."
                        : "Select additional communities to include in this leaderboard."}
                    </div>

                    {ownedCircles.length === 0 ? (
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>
                        {rtlMode
                          ? "אין קהילות נוספות בבעלותך."
                          : "You have no other communities."}
                      </div>
                    ) : (
                      <div
                        style={{
                          maxHeight: 190,
                          overflowY: "auto",
                          padding: "4px 0",
                        }}
                      >
                        {ownedCircles.map((circle) => {
                          const id = String(circle._id);

                          const selectedIds = competitionCircleIdsText
                            .split(",")
                            .map((value) => value.trim())
                            .filter(Boolean);

                          return (
                            <SettingRow
                              key={id}
                              style={{
                                padding: "7px 0",
                                borderBottom:
                                  "1px solid rgba(255,255,255,0.06)",
                              }}
                            >
                              <span>{circle.name || "Community"}</span>

                              <Switch
                                type="checkbox"
                                checked={selectedIds.includes(id)}
                                disabled={savingSettings}
                                onChange={(event) => {
                                  const checked = event.target.checked;

                                  setCompetitionCircleIdsText((current) => {
                                    const ids = new Set(
                                      current
                                        .split(",")
                                        .map((value) => value.trim())
                                        .filter(Boolean),
                                    );

                                    if (checked) {
                                      ids.add(id);
                                    } else {
                                      ids.delete(id);
                                    }

                                    return [...ids].join(", ");
                                  });

                                  setSettingsChanged(true);
                                }}
                              />
                            </SettingRow>
                          );
                        })}
                      </div>
                    )}
                  </SettingColumn>

                  <SettingColumn>
                    <SettingLabel>
                      {rtlMode ? "מנצחי הצעדים" : "Step Winners"}
                    </SettingLabel>

                    <SettingRow>
                      <span>
                        {rtlMode
                          ? "הצגת מנצחי השבוע שעבר"
                          : "Show last week’s winners"}
                      </span>

                      <Switch
                        type="checkbox"
                        checked={showWeeklyWinners}
                        onChange={(event) => {
                          setShowWeeklyWinners(event.target.checked);
                          setSettingsChanged(true);
                        }}
                      />
                    </SettingRow>

                    <ActivitySelect
                      value={weeklyWinnersIntervalMinutes}
                      disabled={!showWeeklyWinners}
                      onChange={(event) => {
                        setWeeklyWinnersIntervalMinutes(
                          Number(event.target.value),
                        );
                        setSettingsChanged(true);
                      }}
                    >
                      {[1, 2, 5, 10, 15].map((minutes) => (
                        <option key={minutes} value={minutes}>
                          {rtlMode
                            ? `כל ${minutes} דקות`
                            : `Every ${minutes} minute${minutes === 1 ? "" : "s"}`}
                        </option>
                      ))}
                    </ActivitySelect>

                    <SettingRow style={{ marginTop: 10 }}>
                      <span>
                        {rtlMode
                          ? "הצגת מובילי כל הזמנים"
                          : "Show all-time leaders"}
                      </span>

                      <Switch
                        type="checkbox"
                        checked={showAllTimeWinners}
                        onChange={(event) => {
                          setShowAllTimeWinners(event.target.checked);
                          setSettingsChanged(true);
                        }}
                      />
                    </SettingRow>

                    <ActivitySelect
                      value={allTimeWinnersIntervalMinutes}
                      disabled={!showAllTimeWinners}
                      onChange={(event) => {
                        setAllTimeWinnersIntervalMinutes(
                          Number(event.target.value),
                        );
                        setSettingsChanged(true);
                      }}
                    >
                      {[1, 2, 5, 10, 15].map((minutes) => (
                        <option key={minutes} value={minutes}>
                          {rtlMode
                            ? `כל ${minutes} דקות`
                            : `Every ${minutes} minute${minutes === 1 ? "" : "s"}`}
                        </option>
                      ))}
                    </ActivitySelect>
                  </SettingColumn>

                  <SettingColumn>
                    <SettingLabel>Refresh Data</SettingLabel>

                    <ActivitySelect
                      value={refreshIntervalMinutes}
                      onChange={(e) => {
                        setRefreshIntervalMinutes(Number(e.target.value));
                        setSettingsChanged(true);
                      }}
                    >
                      <option value={1}>Every minute</option>
                      <option value={2}>Every 2 minutes</option>
                      <option value={5}>Every 5 minutes</option>
                      <option value={10}>Every 10 minutes</option>
                      <option value={15}>Every 15 minutes</option>
                    </ActivitySelect>
                  </SettingColumn>

                  <SettingColumn>
                    <SettingLabel>Leaderboard Members</SettingLabel>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "6px",
                      }}
                    >
                      <div>
                        <SettingLabel>Keep at top</SettingLabel>

                        <ActivitySelect
                          value={topLeadersCount}
                          onChange={(e) => {
                            setTopLeadersCount(Number(e.target.value));
                            setMemberPage(0);
                            setSettingsChanged(true);
                          }}
                        >
                          <option value={1}>Top 1</option>
                          <option value={2}>Top 2</option>
                          <option value={3}>Top 3</option>
                          <option value={4}>Top 4</option>
                          <option value={5}>Top 5</option>
                        </ActivitySelect>
                      </div>

                      <div>
                        <SettingLabel>Rows per group</SettingLabel>

                        <ActivitySelect
                          value={rotatingRowsCount}
                          onChange={(e) => {
                            setRotatingRowsCount(Number(e.target.value));
                            setMemberPage(0);
                            setSettingsChanged(true);
                          }}
                        >
                          <option value={3}>3 rows</option>
                          <option value={4}>4 rows</option>
                          <option value={5}>5 rows</option>
                          <option value={6}>6 rows</option>
                          <option value={7}>7 rows</option>
                          <option value={8}>8 rows</option>
                          <option value={9}>9 rows</option>
                          <option value={10}>10 rows</option>
                        </ActivitySelect>
                      </div>
                    </div>

                    <div style={{ marginTop: "5px" }}>
                      <SettingLabel>Rotate members</SettingLabel>

                      <ActivitySelect
                        value={memberRotationSeconds}
                        onChange={(e) => {
                          setMemberRotationSeconds(Number(e.target.value));
                          setMemberPage(0);
                          setSettingsChanged(true);
                        }}
                      >
                        <option value={0}>Off - don't rotate</option>
                        <option value={5}>Every 5 seconds</option>
                        <option value={10}>Every 10 seconds</option>
                        <option value={15}>Every 15 seconds</option>
                        <option value={20}>Every 20 seconds</option>
                        <option value={30}>Every 30 seconds</option>
                        <option value={45}>Every 45 seconds</option>
                        <option value={60}>Every 60 seconds</option>
                      </ActivitySelect>
                    </div>
                  </SettingColumn>

                  <SettingRow>
                    <span>Show QR code</span>

                    <Switch
                      type="checkbox"
                      checked={showQR}
                      onChange={(e) => {
                        setShowQR(e.target.checked);
                        setSettingsChanged(true);
                      }}
                    />
                  </SettingRow>

                  <SettingRow>
                    <span>Exclude circle admin</span>

                    <Switch
                      type="checkbox"
                      checked={excludeAdmin}
                      onChange={(e) => {
                        setExcludeAdmin(e.target.checked);
                        setSettingsChanged(true);
                      }}
                    />
                  </SettingRow>

                  <SettingRow>
                    <span>Hebrew mode / מצב עברית</span>

                    <Switch
                      type="checkbox"
                      checked={rtlMode}
                      onChange={(e) => {
                        setRtlMode(e.target.checked);
                        setSettingsChanged(true);
                      }}
                    />
                  </SettingRow>

                  <SettingColumn>
                    <SettingLabel>TV Display Size</SettingLabel>

                    <ActivitySelect
                      value={autoFit ? "auto" : String(displayScale)}
                      disabled={savingSettings}
                      onChange={(event) => {
                        const value = event.target.value;

                        setAutoFit(value === "auto");

                        if (value !== "auto") {
                          setDisplayScale(Number(value));
                        }

                        setSettingsChanged(true);
                      }}
                    >
                      <option value="auto">
                        {rtlMode
                          ? "אוטומטי — התאמה למסך"
                          : "Automatic — fit screen"}
                      </option>

                      {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10].map(
                        (percentage) => (
                          <option key={percentage} value={String(percentage)}>
                            {percentage}%
                          </option>
                        ),
                      )}
                    </ActivitySelect>
                  </SettingColumn>

                  <SettingColumn>
                    <SettingLabel>Activity</SettingLabel>

                    <ActivitySelect
                      value={selectedActivityId}
                      onChange={(e) => {
                        setSelectedActivityId(e.target.value);
                        setSettingsChanged(true);
                      }}
                    >
                      <option value="all">All activities in this circle</option>

                      {circleActivities.map((activity) => (
                        <option key={activity.id} value={activity.id}>
                          {activity.title}
                        </option>
                      ))}
                    </ActivitySelect>
                  </SettingColumn>

                  <SettingColumn>
                    <SettingLabel>Time Frame</SettingLabel>

                    <ActivitySelect
                      value={notesTimeFrame}
                      onChange={(e) => {
                        setNotesTimeFrame(e.target.value);
                        setSettingsChanged(true);
                      }}
                    >
                      <option value="1">Current day</option>
                      <option value="7">Last 7 days</option>
                      <option value="30">Last 30 days</option>
                      <option value="60">Last 60 days</option>
                      <option value="90">Last 90 days</option>
                      <option value="all">All time</option>
                      <option value="custom">Custom dates</option>
                    </ActivitySelect>

                    {notesTimeFrame === "custom" && (
                      <>
                        <DateRangeGrid>
                          <DateField>
                            <span>Start date</span>

                            <DateInput
                              type="date"
                              value={fromDate}
                              max={toDate || undefined}
                              onChange={(e) => {
                                setFromDate(e.target.value);
                                setSettingsChanged(true);
                              }}
                            />
                          </DateField>

                          <DateField>
                            <span>End date</span>

                            <DateInput
                              type="date"
                              value={toDate}
                              min={fromDate || undefined}
                              onChange={(e) => {
                                setToDate(e.target.value);
                                setSettingsChanged(true);
                              }}
                            />
                          </DateField>
                        </DateRangeGrid>

                        {hasInvalidDateRange && (
                          <DateError>
                            Please select a valid start and end date.
                          </DateError>
                        )}
                      </>
                    )}
                  </SettingColumn>

                  <SettingColumn>
                    <SettingLabel>Visible Metrics</SettingLabel>

                    <MetricsGrid>
                      <MetricItem>
                        <Switch
                          type="checkbox"
                          checked={
                            circleData?.showSteps === true &&
                            visibleMetrics.steps
                          }
                          disabled={circleData?.showSteps !== true}
                          onChange={(event) =>
                            updateVisibleMetric("steps", event.target.checked)
                          }
                        />

                        <span>
                          Steps
                          {circleData?.showSteps !== true ? " (disabled)" : ""}
                        </span>
                      </MetricItem>

                      <MetricItem>
                        <Switch
                          type="checkbox"
                          checked={visibleMetrics.distance}
                          onChange={(e) =>
                            updateVisibleMetric("distance", e.target.checked)
                          }
                        />
                        <span>Distance</span>
                      </MetricItem>

                      <MetricItem>
                        <Switch
                          type="checkbox"
                          checked={visibleMetrics.points}
                          onChange={(e) =>
                            updateVisibleMetric("points", e.target.checked)
                          }
                        />
                        <span>Points</span>
                      </MetricItem>

                      <MetricItem>
                        <Switch
                          type="checkbox"
                          checked={visibleMetrics.checkins}
                          onChange={(e) =>
                            updateVisibleMetric("checkins", e.target.checked)
                          }
                        />
                        <span>Check-ins</span>
                      </MetricItem>
                    </MetricsGrid>
                  </SettingColumn>

                  {/**         
            <BackButton
              type="button"
              onClick={() => props.history.push(`/circle/${circleId}`)}
            >
              ← Back to Circle
            </BackButton>
            */}
                </SettingsLayout>

                <SettingsFooter>
                  {settingsError && (
                    <div
                      role="alert"
                      style={{
                        marginBottom: 8,
                        padding: "10px",
                        borderRadius: 8,
                        background: "rgba(239, 68, 68, 0.15)",
                        border: "1px solid rgba(239, 68, 68, 0.4)",
                        color: "#fca5a5",
                        fontSize: 12,
                        lineHeight: 1.5,
                        direction: "ltr",
                        textAlign: "left",
                        whiteSpace: "normal",
                      }}
                    >
                      {settingsError}
                    </div>
                  )}

                  <CloseModalButton
                    type="button"
                    style={{ position: "static", marginTop: 0 }}
                    disabled={
                      savingSettings || hasInvalidDateRange || !settingsChanged
                    }
                    onClick={saveLeaderboardSettings}
                  >
                    {savingSettings ? "Saving..." : "Save settings"}
                  </CloseModalButton>
                </SettingsFooter>
              </>
            )}
          </ModalCard>
        </ModalOverlay>
      )}

      {activeWinnerModal && winnersEnabled && !showSettingsModal && (
        <WinnersOverlay
          $closing={winnerModalClosing}
          onClick={() => setWinnerModalClosing(true)}
        >
          <FitToScreen automatic maxWidth={680}>
            <WinnersCard
              $rtl={rtlMode}
              role="dialog"
              aria-modal="true"
              aria-labelledby="winners-modal-title"
              onClick={(event) => event.stopPropagation()}
            >
              <WinnersClose
                type="button"
                aria-label={rtlMode ? "סגירה" : "Close"}
                onClick={() => setActiveWinnerModal(null)}
              >
                ×
              </WinnersClose>

              <div style={{ fontSize: 32 }}>🏆</div>

              <h2
                id="winners-modal-title"
                style={{
                  margin: "12px 0 8px",
                  fontSize: "clamp(18px, 2.5vw, 23px)",
                  lineHeight: 1.3,
                }}
              >
                {activeWinnerModal.type === "weekly"
                  ? rtlMode
                    ? "אלופי הצעדים של השבוע שעבר"
                    : "Last Week’s Step Champions"
                  : rtlMode
                    ? "מובילי הצעדים בכל הזמנים"
                    : "All-Time Step Leaders"}
              </h2>

              {activeWinnerModal.type === "weekly" && activeWinnerModal.week ? (
                <WinnersPeriod>
                  <small>{rtlMode ? "תקופת הדירוג" : "RANKING PERIOD"}</small>

                  <strong dir="ltr">
                    {new Date(
                      `${activeWinnerModal.week.fromDate}T12:00:00`,
                    ).toLocaleDateString("en-GB")}
                    {" – "}
                    {new Date(
                      `${activeWinnerModal.week.toDate}T12:00:00`,
                    ).toLocaleDateString("en-GB")}
                  </strong>
                </WinnersPeriod>
              ) : (
                <WinnersPeriod>
                  <strong>{rtlMode ? "כל הזמנים" : "ALL TIME"}</strong>
                </WinnersPeriod>
              )}

              <WinnersGrid $count={activeWinnerModal.users.length}>
                {activeWinnerModal.users.map((user, index) => (
                  <WinnerUser key={user.id} $index={index}>
                    <WinnerRank $rank={index + 1}>{index + 1}</WinnerRank>

                    <WinnerAvatar
                      $rank={index + 1}
                      src={user.image || DEFAULT_AVATAR}
                      alt={user.nickname}
                      onError={(event) => {
                        if (
                          event.currentTarget.getAttribute("src") !==
                          DEFAULT_AVATAR
                        ) {
                          event.currentTarget.src = DEFAULT_AVATAR;
                        }
                      }}
                    />

                    <WinnerName title={user.nickname}>
                      <bdi dir="auto">{user.nickname}</bdi>
                    </WinnerName>
                    {user.circleName && (
                      <CircleTag
                        $color={
                          clubColors[String(user.circleId || circleData?._id)]
                        }
                      >
                        {user.circleName}
                      </CircleTag>
                    )}

                    <WinnerSteps>
                      {user.steps.toLocaleString(rtlMode ? "he-IL" : "en-US")}
                      <small>{rtlMode ? "צעדים" : "steps"}</small>
                    </WinnerSteps>
                  </WinnerUser>
                ))}
              </WinnersGrid>
            </WinnersCard>
          </FitToScreen>
        </WinnersOverlay>
      )}

      {joinedUserToast && (
        <JoinToast>
          <JoinToastAvatar
            src={joinedUserToast.image || DEFAULT_AVATAR}
            alt={joinedUserToast.nickname || "User"}
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = DEFAULT_AVATAR;
            }}
          />

          <JoinToastText>
            <JoinToastTitle>
              🎉 @{joinedUserToast.nickname} joined the circle
            </JoinToastTitle>

            <JoinToastSub>Welcome aboard 👋</JoinToastSub>
          </JoinToastText>
        </JoinToast>
      )}
    </Page>
  );
}

export default CircleLeaderboard;
